import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, hatsAPI, tradesAPI, profileAPI } from '../services/api';
import PasswordChangeModal from './PasswordChangeModal';
import DeletedAccountsAdmin from './DeletedAccountsAdmin';
import ManualDepositsAdmin from './ManualDepositsAdmin';
import { safeNumber } from '../utils/calculations';
import ImageLightbox from './ImageLightbox';

// Inline styles for trade details and modals
const modalStyles = {
  tradeDetails: {
    padding: '20px'
  },
  tradeInfoSection: {
    marginBottom: '20px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    marginTop: '10px'
  },
  infoItem: {
    padding: '8px',
    background: 'white',
    borderRadius: '4px',
    border: '1px solid #dee2e6'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #dee2e6'
  },
  itemNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '60px',
    height: '60px',
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    marginRight: '15px'
  },
  itemNumberBadge: {
    background: '#007bff',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold'
  }
};

const AdminModal = ({ isOpen, onClose, onLogout }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [itemsSearchTimeout, setItemsSearchTimeout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Data states
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [trades, setTrades] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userItems, setUserItems] = useState([]);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [selectedItemForPosition, setSelectedItemForPosition] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedUserForProfile, setSelectedUserForProfile] = useState(null);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [allUserTransactions, setAllUserTransactions] = useState([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [loadingMoreTransactions, setLoadingMoreTransactions] = useState(false);
  const [userProfileData, setUserProfileData] = useState(null);
  const [tradeFee, setTradeFee] = useState(5);
  const [pickupFee, setPickupFee] = useState(5);
  const [firstTradeFreeEnabled, setFirstTradeFreeEnabled] = useState(false);
  const [firstTradeFreeStartDate, setFirstTradeFreeStartDate] = useState('');
  const [firstTradeFreeEndDate, setFirstTradeFreeEndDate] = useState('');
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawStatus, setWithdrawStatus] = useState('pending');
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [tradeDetails, setTradeDetails] = useState(null);
  const [loadingTradeDetails, setLoadingTradeDetails] = useState(false);
  const [showUserTradeDetails, setShowUserTradeDetails] = useState(false);
  const [userTradeDetails, setUserTradeDetails] = useState(null);
  const [tradeSearchTerm, setTradeSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [originalTrades, setOriginalTrades] = useState([]);
  const [filteredManagementTrades, setFilteredManagementTrades] = useState([]);
  const [managementSearchTimeout, setManagementSearchTimeout] = useState(null);
  const [pickupTrades, setPickupTrades] = useState([]);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [disputeDetails, setDisputeDetails] = useState(null);
  // User messages (user-to-admin support)
  const [userMessagesList, setUserMessagesList] = useState([]);
  const [selectedUserForMessages, setSelectedUserForMessages] = useState(null);
  const [userMessageThread, setUserMessageThread] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  
  // Dispute messaging states
  const [showDisputeMessagingModal, setShowDisputeMessagingModal] = useState(false);
  const [selectedDisputeForMessaging, setSelectedDisputeForMessaging] = useState(null);
  const [disputeMessages, setDisputeMessages] = useState([]);
  const [newDisputeMessage, setNewDisputeMessage] = useState('');
  const [loadingDisputeMessages, setLoadingDisputeMessages] = useState(false);
  const [pendingDepositsCount, setPendingDepositsCount] = useState(0);
  const [pendingWithdrawalsCount, setPendingWithdrawalsCount] = useState(0);
  const [pendingItemsCount, setPendingItemsCount] = useState(0);
  const [showClosedDisputes, setShowClosedDisputes] = useState(false);
  const [pickupStatusFilter, setPickupStatusFilter] = useState('all');
  
  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxItemName, setLightboxItemName] = useState('');

  // Image lightbox functions
  const openImageLightbox = (item) => {
    const images = [item.image_url].filter(Boolean);
    if (images.length > 0) {
      setLightboxImages(images);
      setLightboxIndex(0);
      setLightboxItemName(item.name);
      setLightboxOpen(true);
    }
  };

  const loadUserMessages = useCallback(async () => {
    try {
      const messages = await adminAPI.getAllUserMessages();
      setUserMessagesList(messages || []);
    } catch (error) {
      console.error('Failed to load user messages:', error);
      setUserMessagesList([]);
    }
  }, []);

  const loadUserMessageThread = useCallback(async (userId) => {
    try {
      const thread = await adminAPI.getUserMessageThread(userId);
      setUserMessageThread(thread || []);
    } catch (error) {
      console.error('Failed to load message thread:', error);
      setUserMessageThread([]);
    }
  }, []);

  const handleReplyToUser = async (userId) => {
    if (!replyMessage.trim()) {
      setError('Please enter a reply message');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await adminAPI.replyToUserMessage(userId, replyMessage.trim());
      setReplyMessage('');
      setSuccess('Reply sent successfully!');
      await loadUserMessageThread(userId);
      await loadUserMessages();
    } catch (error) {
      console.error('Failed to send reply:', error);
      setError(error.message || 'Failed to send reply');
    } finally {
      setIsLoading(false);
    }
  };

  const loadWithdrawals = useCallback(async () => {
    setIsLoading(true);
    setError(''); setSuccess('');
    try {
      const res = await adminAPI.getWithdrawals(withdrawStatus ? { status: withdrawStatus } : {});
      const withdrawalsData = Array.isArray(res) ? res : [];
      setWithdrawals(withdrawalsData);
      setPendingWithdrawalsCount(withdrawalsData.filter(w => w.status === 'pending').length);
    } catch (e) {
      setError(e.message || 'Failed to load withdrawals');
    } finally { setIsLoading(false); }
  }, [withdrawStatus]);

  const loadPickupTrades = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      // Load trades with pickup delivery method and optional status filter
      const params = { 
        page: 1, 
        per_page: 100,
        delivery: 'pickup'
      };
      
      // Add status filter if not 'all'
      if (pickupStatusFilter !== 'all') {
        params.status = pickupStatusFilter;
      }
      
      const tradesData = await adminAPI.getAllTradesPaged(params);
      
      if (tradesData && Array.isArray(tradesData.items)) {
        setPickupTrades(tradesData.items);
      } else {
        setPickupTrades([]);
      }
    } catch (e) {
      setError(e.message || 'Failed to load pickup trades');
      setPickupTrades([]);
    } finally {
      setIsLoading(false);
    }
  }, [pickupStatusFilter]);

  const loadAdminData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load essential data first (users, settings, pending deposits count)
      const [usersData, settings, pendingDeposits] = await Promise.all([
        adminAPI.getAllUsers().catch(() => []),
        adminAPI.getSettings().catch(() => ({ trade_fee: 5, pickup_fee: 5 })),
        adminAPI.getManualDeposits('pending').catch(() => [])
      ]);

      // Load items with optimized approach
      let itemsData = [];
      try {
        itemsData = await adminAPI.getAllItems();
      } catch (error) {
        console.log('Items loading failed, using fallback:', error);
        // Fallback: load basic items without owner details
        try {
          const publicHats = await hatsAPI.getAll();
          if (publicHats && Array.isArray(publicHats.items)) {
            itemsData = publicHats.items.map(h => ({
              _id: h._id,
              name: h.name,
              description: h.description,
              brand: h.brand,
              size: h.size,
              condition: h.condition,
              image_url: h.image_url,
              image: h.image,
              user_id: h.user_id,
              status: h.status || 'active',
              value: h.value || 0,
              owner: null,
            }));
          }
        } catch (e) {
          console.error('Fallback loading failed:', e);
        }
      }

      // Load basic trades data (without enrichment)
      const tradesPage = await adminAPI.getAllTradesPaged({ page: 1, per_page: 50 }).catch(() => ({ items: [] }));
      const tradesData = Array.isArray(tradesPage?.items) ? tradesPage.items : Array.isArray(tradesPage) ? tradesPage : [];
      
      // Load disputes (non-critical, can be loaded later)
      const disputesData = await adminAPI.getAllDisputes().catch(() => []);
      
      setUsers(usersData);
      setItems(itemsData);
      setFilteredItems(itemsData);
      setTrades(tradesData);
      setDisputes(disputesData);
      setTradeFee(settings.trade_fee ?? 5);
      setPendingDepositsCount(pendingDeposits.deposits?.length || 0);
      setPendingItemsCount(itemsData.filter(item => item.status === 'pending').length);
      setPickupFee(settings.pickup_fee ?? 5);
      setFirstTradeFreeEnabled(settings.first_trade_free_enabled ?? false);
      // Convert ISO date strings to local date format for input fields (YYYY-MM-DD)
      if (settings.first_trade_free_start_date) {
        const startDate = new Date(settings.first_trade_free_start_date);
        setFirstTradeFreeStartDate(startDate.toISOString().split('T')[0]);
      } else {
        setFirstTradeFreeStartDate('');
      }
      if (settings.first_trade_free_end_date) {
        const endDate = new Date(settings.first_trade_free_end_date);
        setFirstTradeFreeEndDate(endDate.toISOString().split('T')[0]);
      } else {
        setFirstTradeFreeEndDate('');
      }
       
       // Set featured items
       const featuredItemsData = itemsData.filter(item => item.featured);
       setFeaturedItems(featuredItemsData);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      setError('Failed to load admin data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to load more transactions for a user
  const loadMoreTransactions = async (userId) => {
    if (loadingMoreTransactions || !hasMoreTransactions) return;
    
    setLoadingMoreTransactions(true);
    try {
      const nextPage = transactionsPage + 1;
      const response = await adminAPI.getTransactions({ 
        user_id: userId, 
        page: nextPage, 
        per_page: 20 
      });
      
      const newTransactions = Array.isArray(response?.transactions) ? response.transactions : Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [];
      
      if (newTransactions.length === 0) {
        setHasMoreTransactions(false);
      } else {
        setAllUserTransactions(prev => [...prev, ...newTransactions]);
        setTransactionsPage(nextPage);
        // Check if we've reached the end based on total count
        const totalLoaded = allUserTransactions.length + newTransactions.length;
        if (totalLoaded >= (response?.total || 0)) {
          setHasMoreTransactions(false);
        }
      }
    } catch (error) {
      console.error('Error loading more transactions:', error);
      setError('Failed to load more transactions');
    } finally {
      setLoadingMoreTransactions(false);
    }
  };

  // Function to load dispute details
  const loadDisputeDetails = async (disputeId) => {
    try {
      const dispute = disputes.find(d => d._id === disputeId);
      if (!dispute) {
        setError('Dispute not found');
        return;
      }
      
      setSelectedDispute(dispute);
      
      // Load detailed dispute information
      const [userData, tradeData] = await Promise.all([
        adminAPI.getUserDetails(dispute.user_id).catch(() => null),
        tradesAPI.get(dispute.trade_id).catch(() => null)
      ]);
      
      setDisputeDetails({
        user: userData,
        trade: tradeData
      });
    } catch (error) {
      console.error('Error loading dispute details:', error);
      setDisputeDetails({
        user: null,
        trade: null
      });
    }
  };

  // Function to load dispute messages
  const loadDisputeMessages = async (disputeId) => {
    try {
      setLoadingDisputeMessages(true);
      const response = await adminAPI.getDisputeMessages(disputeId);
      setDisputeMessages(response.messages || []);
    } catch (error) {
      console.error('Error loading dispute messages:', error);
      setError('Failed to load dispute messages');
      setDisputeMessages([]);
    } finally {
      setLoadingDisputeMessages(false);
    }
  };

  // Function to send dispute message
  const sendDisputeMessage = async () => {
    if (!newDisputeMessage.trim() || !selectedDisputeForMessaging) return;
    
    try {
      await adminAPI.sendDisputeMessage(selectedDisputeForMessaging, newDisputeMessage.trim());
      setNewDisputeMessage('');
      // Reload messages to show the new one
      await loadDisputeMessages(selectedDisputeForMessaging);
    } catch (error) {
      console.error('Error sending dispute message:', error);
      setError('Failed to send message');
    }
  };

  // Function to open dispute messaging modal
  const openDisputeMessaging = async (disputeId) => {
    setSelectedDisputeForMessaging(disputeId);
    setShowDisputeMessagingModal(true);
    await loadDisputeMessages(disputeId);
  };

  // Admin override functions for tracking confirmations
  const adminOverrideReceived = async (userId, userName) => {
    if (!window.confirm(`Admin Override: Mark item as received for ${userName}?\n\nThis will force the system to acknowledge that ${userName} has received their item.`)) {
      return;
    }
    
    try {
      setIsLoading(true);
      await adminAPI.adminOverrideReceived(selectedTrade._id, userId);
      setSuccess(`Admin override: ${userName} marked as received item`);
      
      // Update the selectedTrade object directly with the new received_at timestamp
      const now = new Date().toISOString();
      const updatedTrade = { ...selectedTrade };
      
      if (userId === selectedTrade.user1_id) {
        updatedTrade.user1_received_at = now;
        updatedTrade.admin_override = { 
          ...updatedTrade.admin_override, 
          action: 'received',
          user_id: userId,
          timestamp: now,
          admin_id: 'current_admin'
        };
      } else if (userId === selectedTrade.user2_id) {
        updatedTrade.user2_received_at = now;
        updatedTrade.admin_override = { 
          ...updatedTrade.admin_override, 
          action: 'received',
          user_id: userId,
          timestamp: now,
          admin_id: 'current_admin'
        };
      }
      
      setSelectedTrade(updatedTrade);
      
    } catch (error) {
      console.error('Error with admin override received:', error);
      setError('Failed to override received status');
    } finally {
      setIsLoading(false);
    }
  };

  const adminOverrideReturned = async (userId, userName) => {
    if (!window.confirm(`Admin Override: Mark item as returned for ${userName}?\n\nThis will force the system to acknowledge that ${userName} has returned their item.`)) {
      return;
    }
    
    try {
      setIsLoading(true);
      await adminAPI.adminOverrideReturned(selectedTrade._id, userId);
      setSuccess(`Admin override: ${userName} marked as returned item`);
      
      // Update the selectedTrade object directly with the new returned_at timestamp
      const now = new Date().toISOString();
      const updatedTrade = { ...selectedTrade };
      
      if (userId === selectedTrade.user1_id) {
        updatedTrade.user1_returned_at = now;
        updatedTrade.admin_override = { 
          ...updatedTrade.admin_override, 
          action: 'returned',
          user_id: userId,
          timestamp: now,
          admin_id: 'current_admin'
        };
      } else if (userId === selectedTrade.user2_id) {
        updatedTrade.user2_returned_at = now;
        updatedTrade.admin_override = { 
          ...updatedTrade.admin_override, 
          action: 'returned',
          user_id: userId,
          timestamp: now,
          admin_id: 'current_admin'
        };
      }
      
      setSelectedTrade(updatedTrade);
      
    } catch (error) {
      console.error('Error with admin override returned:', error);
      setError('Failed to override returned status');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to open trade details modal
  const openTradeDetails = async (trade) => {
    try {
      setShowTradeModal(true);
      setLoadingTradeDetails(true);
      
      // Fetch fresh trade data from the database
      const freshTradeData = await adminAPI.getTradeDetails(trade._id);
      setSelectedTrade(freshTradeData.trade);
      
      // Load detailed trade information
      const [user1Data, user2Data, item1Data, item2Data] = await Promise.all([
        adminAPI.getUserDetails(trade.user1_id).catch(() => null),
        adminAPI.getUserDetails(trade.user2_id).catch(() => null),
        hatsAPI.get(trade.hat1_id).catch(() => null),
        hatsAPI.get(trade.hat2_id).catch(() => null)
      ]);
      
      setTradeDetails({
        user1: user1Data,
        user2: user2Data,
        item1: item1Data,
        item2: item2Data
      });
    } catch (error) {
      console.error('Error loading trade details:', error);
      setTradeDetails({
        user1: null,
        user2: null,
        item1: null,
        item2: null
      });
    } finally {
      setLoadingTradeDetails(false);
    }
  };

  const tabs = [
    { id: 'users', label: 'Users', icon: 'fas fa-users' },
    { id: 'items', label: 'Items', icon: 'fas fa-hat-cowboy' },
    { id: 'featured', label: 'Featured Items', icon: 'fas fa-star' },
    { id: 'trades', label: 'Trades', icon: 'fas fa-exchange-alt' },
    { id: 'pickup-tracking', label: 'Pickup Tracking', icon: 'fas fa-truck' },
    { id: 'disputes', label: 'Disputes', icon: 'fas fa-exclamation-triangle' },
    { id: 'balance', label: 'Balance', icon: 'fas fa-dollar-sign' },
    { id: 'withdrawals', label: 'Withdrawals', icon: 'fas fa-money-bill-wave' },
    { id: 'manual-deposits', label: 'Manual Deposits', icon: 'fas fa-credit-card' },
    { id: 'user-messages', label: 'User Messages', icon: 'fas fa-envelope' },
    { id: 'deleted-accounts', label: 'Deleted Accounts', icon: 'fas fa-user-times' },
    { id: 'settings', label: 'Settings', icon: 'fas fa-cog' },
    { id: 'profile', label: 'Profile', icon: 'fas fa-user-cog' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadAdminData();
      loadWithdrawals(); // Load withdrawals on initial open to get badge count
    }
  }, [isOpen, loadWithdrawals, loadAdminData]);

  // Lazy load Trades tab data only when needed
  useEffect(() => {
    if (isOpen && activeTab === 'trades' && trades.length === 0) {
      loadAdminTrades();
    }
  }, [isOpen, activeTab, trades.length]);

  // Lazy load Pickup Tracking tab data only when needed
  useEffect(() => {
    if (isOpen && activeTab === 'pickup-tracking') {
      loadPickupTrades();
    }
  }, [isOpen, activeTab, loadPickupTrades]);

  useEffect(()=>{ if (isOpen && activeTab === 'withdrawals') loadWithdrawals(); }, [isOpen, activeTab, loadWithdrawals]);

  const loadAdminTrades = async (filters = {}) => {
    setIsLoading(true);
    try {
      const params = { page: 1, per_page: 100, ...filters };
      const page = await adminAPI.getAllTradesPaged(params);
      const items = Array.isArray(page?.items) ? page.items : Array.isArray(page) ? page : [];
      
      // Optimize: Only enrich trades that are actually displayed
      // For now, just use the basic trade data without enrichment
      // Enrichment can be done on-demand when viewing trade details
      setTrades(items);
      setOriginalTrades(items);
      setFilteredManagementTrades(items);
    } catch (e) {
      setError(e.message || 'Failed to load trades');
    } finally {
      setIsLoading(false);
    }
  };



  const handleTradeSearch = async () => {
    setIsLoading(true);
    try {
      // Use original trades for filtering
      let filteredTrades = [...originalTrades];
      
      // Process Trade ID search
      if (tradeSearchTerm && tradeSearchTerm.trim()) {
        let tradeId = tradeSearchTerm.trim();
        if (tradeId.toLowerCase().includes('id:')) {
          const idMatch = tradeId.toLowerCase().match(/id:\s*([a-f0-9]+)/);
          if (idMatch && idMatch[1].length >= 6) {
            tradeId = idMatch[1];
          }
        }
        if (/^[a-fA-F0-9]{6,}$/.test(tradeId)) {
          filteredTrades = filteredTrades.filter(trade => 
            trade._id && trade._id.toLowerCase().endsWith(tradeId.toLowerCase())
          );
        }
      }
      
      // Process User ID search
      if (userSearchTerm && userSearchTerm.trim()) {
        let userId = userSearchTerm.trim();
        if (userId.toLowerCase().includes('id:')) {
          const idMatch = userId.toLowerCase().match(/id:\s*([a-f0-9]+)/);
          if (idMatch && idMatch[1].length >= 6) {
            userId = idMatch[1];
          }
        }
        if (/^[a-fA-F0-9]{6,}$/.test(userId)) {
          filteredTrades = filteredTrades.filter(trade => 
            (trade.user1_id && trade.user1_id.toLowerCase().endsWith(userId.toLowerCase())) ||
            (trade.user2_id && trade.user2_id.toLowerCase().endsWith(userId.toLowerCase()))
          );
        }
      }
      
      setTrades(filteredTrades);
    } catch (e) {
      setError(e.message || 'Failed to search trades');
    } finally {
      setIsLoading(false);
    }
  };

  // Real-time filtering for Management tab
  const filterManagementTrades = (tradeSearch, userSearch) => {
    if (!originalTrades.length) return;
    
    let filtered = [...originalTrades];
    
    // Filter by trade_id
    if (tradeSearch) {
      const tradeSearchLower = tradeSearch.toLowerCase();
      filtered = filtered.filter(trade => {
        const tradeId = String(trade._id || '').toLowerCase();
        if (tradeSearchLower.includes('id:')) {
          const idMatch = tradeSearchLower.match(/id:\s*([a-f0-9]+)/);
          if (idMatch && idMatch[1].length >= 6) {
            return tradeId.endsWith(idMatch[1]);
          }
        } else if (tradeSearchLower.length >= 6 && /^[a-f0-9]+$/.test(tradeSearchLower)) {
          return tradeId.endsWith(tradeSearchLower);
        }
        return tradeId.includes(tradeSearchLower);
      });
    }
    
    // Filter by user_id (searches both users and their names)
    if (userSearch) {
      const userSearchLower = userSearch.toLowerCase();
      filtered = filtered.filter(trade => {
        const user1Id = String(trade.user1_id || '').toLowerCase();
        const user2Id = String(trade.user2_id || '').toLowerCase();
        const user1Name = String(trade.user1_name || '').toLowerCase();
        const user2Name = String(trade.user2_name || '').toLowerCase();
        
        if (userSearchLower.includes('id:')) {
          const idMatch = userSearchLower.match(/id:\s*([a-f0-9]+)/);
          if (idMatch && idMatch[1].length >= 6) {
            return user1Id.endsWith(idMatch[1]) || user2Id.endsWith(idMatch[1]);
          }
          return false;
        } else if (userSearchLower.length >= 6 && /^[a-f0-9]+$/.test(userSearchLower)) {
          return user1Id.endsWith(userSearchLower) || user2Id.endsWith(userSearchLower);
        } else {
          return user1Id.includes(userSearchLower) || 
                 user2Id.includes(userSearchLower) ||
                 user1Name.includes(userSearchLower) || 
                 user2Name.includes(userSearchLower);
        }
      });
    }
    
    setFilteredManagementTrades(filtered);
  };

  // Debounced search handler for Management tab
  const handleManagementFilterChange = (field, value) => {
    const newTradeSearch = field === 'trade' ? value : tradeSearchTerm;
    const newUserSearch = field === 'user' ? value : userSearchTerm;
    
    if (field === 'trade') {
      setTradeSearchTerm(value);
    } else if (field === 'user') {
      setUserSearchTerm(value);
    }
    
    // Clear existing timeout
    if (managementSearchTimeout) {
      clearTimeout(managementSearchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      filterManagementTrades(newTradeSearch, newUserSearch);
    }, 300);
    
    setManagementSearchTimeout(timeout);
  };

  useEffect(() => { if (isOpen && activeTab === 'trades') loadAdminTrades(); }, [isOpen, activeTab]);
  useEffect(() => { if (isOpen && activeTab === 'user-messages') loadUserMessages(); }, [isOpen, activeTab, loadUserMessages]);
  useEffect(() => { 
    if (isOpen && activeTab === 'user-messages' && selectedUserForMessages) {
      loadUserMessageThread(selectedUserForMessages);
    }
  }, [isOpen, activeTab, selectedUserForMessages, loadUserMessageThread]);

  // Helper to resolve user identifier to ObjectId string (from loaded users by email)
  const resolveUserId = (raw) => {
    try {
      const val = String(raw || '').trim();
      if (!val) return '';
      
      // Handle "ID:" prefix
      if (val.toLowerCase().includes('id:')) {
        const idMatch = val.toLowerCase().match(/id:\s*([a-f0-9]+)/);
        if (idMatch && idMatch[1].length >= 6) {
          // If it's a full 24-char ID, return it directly
          if (idMatch[1].length === 24) return idMatch[1];
          // If it's a partial ID, try to find matching users
          const matchingUser = (users || []).find(u => 
            u._id && u._id.toLowerCase().endsWith(idMatch[1].toLowerCase())
          );
          return matchingUser?._id || '';
        }
      }
      
      // If looks like 24-char hex, assume ObjectId
      if (/^[a-fA-F0-9]{24}$/.test(val)) return val;
      
      // If looks like email, try to find in loaded users
      if (val.includes('@')) {
        const u = (users || []).find(u => String(u.email || '').toLowerCase() === val.toLowerCase());
        return u?._id || '';
      }
      
      // If it's a partial ID (6+ hex chars), try to find matching users
      if (/^[a-fA-F0-9]{6,}$/.test(val)) {
        const matchingUser = (users || []).find(u => 
          u._id && u._id.toLowerCase().endsWith(val.toLowerCase())
        );
        return matchingUser?._id || '';
      }
      
      return '';
    } catch { return ''; }
  };

  // Admin Balance Tab state
  const [adminTxFilters, setAdminTxFilters] = useState({ user_id: '', type: '', direction: '', date_from: '', date_to: '', trade_id: '' });
  const [adminTxPage, setAdminTxPage] = useState(1);
  const [adminTxData, setAdminTxData] = useState({ transactions: [], totals: { debit: { total: 0, count: 0 }, credit: { total: 0, count: 0 } }, total: 0, per_page: 50, page: 1 });
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const loadAdminTransactions = useCallback(async (pageOverride, filtersOverride) => {
    setIsLoading(true);
    try {
      const baseFilters = filtersOverride ? { ...filtersOverride } : { ...adminTxFilters };
      
      // Process "ID:" prefix for user_id and trade_id
      if (baseFilters.user_id && baseFilters.user_id.toLowerCase().includes('id:')) {
        const idMatch = baseFilters.user_id.toLowerCase().match(/id:\s*([a-f0-9]+)/);
        if (idMatch && idMatch[1].length >= 6) {
          baseFilters.user_id = idMatch[1];
        }
      }
      
      if (baseFilters.trade_id) {
        let tradeId = baseFilters.trade_id;
        // Handle "ID:" prefix format
        if (tradeId.toLowerCase().includes('id:')) {
          const idMatch = tradeId.toLowerCase().match(/id:\s*([a-f0-9]+)/);
          if (idMatch && idMatch[1].length >= 6) {
            tradeId = idMatch[1];
          }
        }
        // Handle direct hex search (6+ characters)
        else if (tradeId.length >= 6 && /^[a-fA-F0-9]+$/.test(tradeId)) {
          tradeId = tradeId.toLowerCase();
        }
        baseFilters.trade_id = tradeId;
      }
      
      const params = { ...baseFilters, page: pageOverride || adminTxPage, per_page: 50 };
      Object.keys(params).forEach(k => (params[k] === '' || params[k] == null) && delete params[k]);
      const res = await adminAPI.getTransactions(params);
      setAdminTxData(res);
      setAllTransactions(res.transactions || []);
      setFilteredTransactions(res.transactions || []);
      if (pageOverride) setAdminTxPage(pageOverride);
    } catch (e) {
      setError(e.message || 'Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  }, [adminTxFilters, adminTxPage]);

  // Real-time filtering function
  const filterTransactions = (filters) => {
    if (!allTransactions.length) return;
    
    let filtered = [...allTransactions];
    
    // Filter by user_id (searches user ID, user name, and user B-ID)
    if (filters.user_id) {
      const userSearch = filters.user_id.toLowerCase();
      filtered = filtered.filter(tx => {
        const userId = String(tx.user_id || '').toLowerCase();
        const userName = String(tx.user_name || '').toLowerCase();
        const userBId = String(tx.user_b_id || '').toLowerCase();
        
        // Handle "ID:" prefix format
        if (userSearch.includes('id:')) {
          const idMatch = userSearch.match(/id:\s*([a-f0-9]+)/);
          if (idMatch && idMatch[1].length >= 6) {
            return userId.endsWith(idMatch[1]) || userBId.endsWith(idMatch[1]);
          }
          return false;
        }
        // Handle direct hex search (6+ characters)
        else if (userSearch.length >= 6 && /^[a-f0-9]+$/.test(userSearch)) {
          return userId.endsWith(userSearch) || userBId.endsWith(userSearch);
        }
        // Handle all other searches (including mixed alphanumeric like B102771)
        else {
          return userId.includes(userSearch) || 
                 userName.includes(userSearch) || 
                 userBId.includes(userSearch);
        }
      });
    }
    
    // Filter by trade_id
    if (filters.trade_id) {
      const tradeSearch = filters.trade_id.toLowerCase();
      filtered = filtered.filter(tx => {
        const tradeId = String(tx.trade_id || '').toLowerCase();
        if (tradeSearch.includes('id:')) {
          const idMatch = tradeSearch.match(/id:\s*([a-f0-9]+)/);
          if (idMatch && idMatch[1].length >= 6) {
            return tradeId.endsWith(idMatch[1]);
          }
          return false;
        } else if (tradeSearch.length >= 6 && /^[a-f0-9]+$/.test(tradeSearch)) {
          return tradeId.endsWith(tradeSearch);
        }
        return tradeId.includes(tradeSearch);
      });
    }
    
    // Filter by type
    if (filters.type) {
      filtered = filtered.filter(tx => tx.type === filters.type);
    }
    
    // Filter by direction
    if (filters.direction) {
      filtered = filtered.filter(tx => tx.direction === filters.direction);
    }
    
    // Filter by date range
    if (filters.date_from) {
      const fromDate = new Date(filters.date_from);
      filtered = filtered.filter(tx => {
        const txDate = new Date(tx.created_at);
        return txDate >= fromDate;
      });
    }
    
    if (filters.date_to) {
      const toDate = new Date(filters.date_to);
      filtered = filtered.filter(tx => {
        const txDate = new Date(tx.created_at);
        return txDate <= toDate;
      });
    }
    
    setFilteredTransactions(filtered);
  };

  // Debounced search handler
  const handleFilterChange = (field, value) => {
    const newFilters = { ...adminTxFilters, [field]: value };
    setAdminTxFilters(newFilters);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      filterTransactions(newFilters);
    }, 300);
    
    setSearchTimeout(timeout);
  };
  useEffect(() => { if (isOpen && activeTab === 'balance') loadAdminTransactions(1); }, [isOpen, activeTab, loadAdminTransactions]);
  const exportAdminTransactions = async () => {
    try {
      const params = { ...adminTxFilters };
      Object.keys(params).forEach(k => (params[k] === '' || params[k] == null) && delete params[k]);
      const blob = await adminAPI.exportTransactionsCSV(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transactions_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Failed to export CSV');
    }
  };

  const loadUserItems = async (userId) => {
    try {
      const [items, details] = await Promise.all([
        adminAPI.getUserItems(userId),
        adminAPI.getUserDetails(userId)
      ]);
      setUserItems(items);
      setSelectedUser(users.find(user => user._id === userId));
      setSelectedUserDetails(details);
      
      // Initialize transactions state for this user
      const recentTransactions = details?.recent_transactions || [];
      setAllUserTransactions(recentTransactions);
      setTransactionsPage(1);
      setHasMoreTransactions(true); // Always show button initially, let API determine if more exist
    } catch (error) {
      console.error('Failed to load user items:', error);
      setError('Failed to load user items');
    }
  };

  const showItemDetails = (item) => {
    setSelectedItem(item);
    setCurrentImageIndex(0); // Reset to first image
    setShowItemModal(true);
  };

  const fetchUserProfile = async (userId) => {
    try {
      const data = await adminAPI.getUserDetails(userId);
      setUserProfileData(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Failed to load user profile');
    }
  };

  const showUserProfile = (user) => {
    setSelectedUserForProfile(user);
    fetchUserProfile(user._id || user.user_id);
    setShowUserProfileModal(true);
  };

  const sendMessage = async () => {
    if (!messageText.trim()) {
      setError('Message cannot be empty');
      return;
    }
    try {
      setIsLoading(true);
      await adminAPI.sendMessage(messageRecipient.id, messageText);
      setSuccess(`Message sent to ${messageRecipient.name} successfully`);
      setShowMessageModal(false);
      setMessageText('');
      try {
        await loadUserMessages();
        if (selectedUserForMessages && String(selectedUserForMessages) === String(messageRecipient.id)) {
          await loadUserMessageThread(messageRecipient.id);
        }
      } catch (_) { /* non-fatal */ }
      setMessageRecipient(null);
    } catch (error) {
      setError(error.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };



  const handleTradeView = async (trade) => {
    setShowTradeModal(true);
    setLoadingTradeDetails(true);
    setTradeDetails(null);
    
    try {
      // Fetch fresh trade data from the database
      const freshTradeData = await adminAPI.getTradeDetails(trade._id);
      setSelectedTrade(freshTradeData.trade);
      
      const details = await loadTradeDetails(freshTradeData.trade);
      setTradeDetails(details);
    } catch (error) {
      console.error('Error loading trade details:', error);
    } finally {
      setLoadingTradeDetails(false);
    }
  };

  const loadTradeDetails = async (trade) => {
    try {
      console.log('Loading trade details for trade:', trade._id);
      console.log('Trade data:', trade);
      

      
      // Load user details and hats
      const [user1Response, user2Response, hat1Response, hat2Response] = await Promise.all([
        adminAPI.getUserDetails(trade.user1_id).catch((e) => {
          console.error('Error loading user1:', e);
          return null;
        }),
        adminAPI.getUserDetails(trade.user2_id).catch((e) => {
          console.error('Error loading user2:', e);
          return null;
        }),
        hatsAPI.get(trade.hat1_id).catch((e) => {
          console.error('Error loading hat1:', e);
          return null;
        }),
        hatsAPI.get(trade.hat2_id).catch((e) => {
          console.error('Error loading hat2:', e);
          return null;
        })
      ]);
      
      console.log('API Responses:', { 
        user1Response, 
        user2Response, 
        hat1Response, 
        hat2Response 
      });
      
      // Extract user data from the nested structure
      const user1 = user1Response?.profile || user1Response;
      const user2 = user2Response?.profile || user2Response;
      
      // Debug hat responses
      console.log('Hat 1 details:', hat1Response);
      console.log('Hat 2 details:', hat2Response);
      
      return { 
        user1, 
        user2, 
        hat1: hat1Response, 
        hat2: hat2Response 
      };
    } catch (error) {
      console.error('Error loading trade details:', error);
      return { user1: null, user2: null, hat1: null, hat2: null };
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setSearchTerm('');
    setSelectedUser(null);
    setUserItems([]);
  };

  const handleAdminAction = async (action, itemName, itemId, itemType = 'user') => {
    setIsLoading(true);
    try {
      if (itemType === 'user') {
        switch(action) {
        case 'disable':
          await adminAPI.updateUserStatus(itemId, 'disabled');
          setSuccess(`${itemName} has been disabled`);
          break;
          case 'enable':
            await adminAPI.updateUserStatus(itemId, 'active');
            setSuccess(`${itemName} has been enabled`);
            break;
          case 'restrict':
            await adminAPI.updateUserStatus(itemId, 'restricted');
            setSuccess(`${itemName} has been restricted to balance access only`);
            break;
          case 'delete':
            if (window.confirm(`Are you sure you want to delete ${itemName}?`)) {
              await adminAPI.deleteUser(itemId);
              setSuccess(`${itemName} has been deleted`);
            }
            break;
          case 'message':
            setMessageRecipient({ id: itemId, name: itemName });
            setShowMessageModal(true);
            setIsLoading(false); // Don't reload data for message action
            return; // Exit early for message action
          default:
            break;
        }
      } else if (itemType === 'trade') {
        switch(action) {
          case 'flag':
            await adminAPI.flagTrade(itemId);
            setSuccess(`${itemName} has been flagged for review`);
            break;
          case 'delete':
            if (window.confirm(`Are you sure you want to delete ${itemName}?`)) {
              await adminAPI.deleteTrade(itemId);
              setSuccess(`${itemName} has been deleted`);
            }
            break;
          default:
            break;
        }
      } else if (itemType === 'dispute') {
        switch(action) {
          case 'resolve':
            const resolution = prompt('Enter resolution details:');
            if (resolution) {
              await adminAPI.resolveDispute(itemId, resolution);
              setSuccess(`${itemName} has been resolved`);
            }
            break;
          case 'message':
            // Open dispute messaging modal
            await openDisputeMessaging(itemId);
            setIsLoading(false);
            return;
          default:
            break;
        }
      } else {
        switch(action) {
          case 'flag':
            await adminAPI.updateItemStatus(itemId, 'flagged');
            setSuccess(`${itemName} has been flagged for review`);
            break;
          case 'approve':
            await adminAPI.updateItemStatus(itemId, 'active');
            setSuccess(`${itemName} has been approved and is now available for trade`);
            break;
          case 'disable':
            await adminAPI.updateItemStatus(itemId, 'disabled');
            setSuccess(`${itemName} has been disabled and is no longer available for trade`);
            break;
          case 'remove':
            if (window.confirm(`Are you sure you want to remove ${itemName}?`)) {
              await adminAPI.deleteItem(itemId);
              setSuccess(`${itemName} has been removed`);
            }
            break;
          case 'resolve':
            const resolution = prompt('Enter resolution details:');
            if (resolution) {
              await adminAPI.resolveDispute(itemId, resolution);
              setSuccess(`${itemName} has been resolved`);
            }
            break;
          case 'message':
            setMessageRecipient({ id: itemId, name: itemName });
            setShowMessageModal(true);
            setIsLoading(false); // Don't reload data for message action
            return; // Exit early for message action
          case 'view':
            // For disputes, we need to load dispute details
            if (itemName.includes('Dispute')) {
              await loadDisputeDetails(itemId);
              setShowDisputeModal(true);
              setIsLoading(false);
              return;
            }
            break;
          default:
            break;
        }
      }
      loadAdminData(); // Reload data
    } catch (error) {
      setError(error.message || `Failed to ${action} ${itemName}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to view trade details from a specific user's perspective
  const viewUserTradeDetails = async (userId, userName) => {
    console.log('=== STARTING viewUserTradeDetails ===');
    console.log('Parameters:', { userId, userName });
    
    try {
      console.log('Opening user trade details for:', { userId, userName });
      
      if (!selectedTrade) {
        console.log('Selected trade not available');
        alert('No trade selected!');
        return;
      }
      
      console.log('Setting modal state...');
      setShowUserTradeDetails(true);
      setUserTradeDetails(null);
      
      // Load user's balance
      console.log('Loading user balance for:', userId);
      let userBalanceData = null;
      try {
        userBalanceData = await adminAPI.getUserBalance(userId);
        console.log('User balance loaded successfully:', userBalanceData);
      } catch (balanceError) {
        console.error('Error loading user balance:', balanceError);
        userBalanceData = { balance: 0 };
      }
      
      // Load the user's actual trade data from their perspective using the admin API
      console.log('Loading user trade data from admin API...');
      console.log('Selected trade ID:', selectedTrade._id);
      console.log('Selected trade object:', selectedTrade);
      
      let userTradeData = null;
      try {
        if (!selectedTrade._id) {
          throw new Error('No trade ID available');
        }
        const tradeResponse = await adminAPI.getTradeDetails(selectedTrade._id);
        userTradeData = tradeResponse.trade;
        console.log('User trade data loaded successfully:', userTradeData);
        console.log('Financial snapshot:', userTradeData.financial_snapshot);
      } catch (tradeError) {
        console.error('Error loading user trade data:', tradeError);
        userTradeData = selectedTrade; // Fallback to admin's trade data
      }
      
      // Create user trade details object with actual user perspective
      const userTradeDetailsData = {
        trade: userTradeData,
        userBalance: userBalanceData?.balance || 0,
        runningBalances: {},
        userPerspective: userId,
        userName: userName,
        userEmail: userBalanceData?.user_email || ''
      };
      
      console.log('Setting user trade details:', userTradeDetailsData);
      setUserTradeDetails(userTradeDetailsData);
      
      console.log('=== viewUserTradeDetails COMPLETED SUCCESSFULLY ===');
      
    } catch (error) {
      console.error('=== viewUserTradeDetails FAILED ===');
      console.error('Error loading user trade details:', error);
      setError('Failed to load user trade details');
      
      // Show basic modal even if API calls fail
      const fallbackUserTradeData = {
        trade: selectedTrade,
        userBalance: 0,
        runningBalances: {},
        userPerspective: userId,
        userName: userName
      };
      console.log('Setting fallback user trade data:', fallbackUserTradeData);
      setUserTradeDetails(fallbackUserTradeData);
    }
  };

  // Calculate running balance for a specific user's pending trades

  const handleUserAction = async (action, userName, userId) => {
    setIsLoading(true);
    try {
      switch(action) {
        case 'view':
          await loadUserItems(userId);
          break;
        case 'disable':
          await adminAPI.updateUserStatus(userId, 'disabled');
          setSuccess(`${userName} has been disabled`);
          break;
        case 'enable':
          await adminAPI.updateUserStatus(userId, 'active');
          setSuccess(`${userName} has been enabled`);
          break;
        case 'restrict':
          await adminAPI.updateUserStatus(userId, 'restricted');
          setSuccess(`${userName} has been restricted to balance access only`);
          break;
        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${userName}?`)) {
            await adminAPI.deleteUser(userId);
            setSuccess(`${userName} has been deleted`);
          }
          break;
        case 'message':
          setMessageRecipient({ id: userId, name: userName });
          setShowMessageModal(true);
          setIsLoading(false); // Don't reload data for message action
          return; // Exit early for message action
        default:
          break;
      }
      loadAdminData(); // Reload data
    } catch (error) {
      setError(error.message || `Failed to ${action} ${userName}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemAction = async (action, itemName, itemId) => {
    setIsLoading(true);
    try {
      switch(action) {
        case 'view':
          const item = items.find(i => i._id === itemId);
          if (item) {
            showItemDetails(item);
          }
          setIsLoading(false); // Don't reload data for view action
          return; // Exit early for view action
        case 'flag':
          await adminAPI.updateItemStatus(itemId, 'flagged');
          setSuccess(`${itemName} has been flagged for review`);
          break;
        case 'approve':
          await adminAPI.updateItemStatus(itemId, 'active');
          setSuccess(`${itemName} has been approved and is now available for trade`);
          break;
        case 'disable':
          await adminAPI.updateItemStatus(itemId, 'disabled');
          setSuccess(`${itemName} has been disabled and is no longer available for trade`);
          break;
        case 'remove':
          if (window.confirm(`Are you sure you want to remove ${itemName}?`)) {
            // Check if this item was featured before deletion
            const itemToDelete = items.find(item => item._id === itemId);
            const wasFeatured = itemToDelete?.featured;
            
            await adminAPI.deleteItem(itemId);
            setSuccess(`${itemName} has been removed`);
            
            // Auto-promote backup items if a featured item was deleted
            if (wasFeatured) {
              await autoPromoteBackupItems();
            }
          }
          break;
        default:
          break;
      }
      loadAdminData(); // Reload data
    } catch (error) {
      setError(error.message || `Failed to ${action} ${itemName}`);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    // Round to 2 decimal places to fix floating-point precision issues
    const roundedAmount = Math.round((amount || 0) * 100) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(roundedAmount);
  };

  const formatMessageDate = (dateString) => {
    try {
      if (!dateString) return 'Invalid Date';
      // Treat the date string as UTC since backend stores in UTC
      const date = new Date(dateString + 'Z'); // Add Z to force UTC interpretation
      
      // Ensure we're working with a valid date
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      // Convert to local timezone and format
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch { return 'Invalid Date'; }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '—';
      const v = dateString;
      let date;
      
      if (typeof v === 'object' && v !== null && ('$date' in v)) {
        date = new Date(v.$date);
      } else {
        date = new Date(v);
      }
      
      // Ensure we're working with a valid date
      if (isNaN(date.getTime())) return '—';
      
      // Convert to local timezone and format
      return date.toLocaleString('en-US', {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch { return '—'; }
  };

  // Helpers for Management tab to normalize possible BSON-serialized shapes
  const idToString = (val) => {
    try {
      if (!val) return '';
      if (typeof val === 'string') return val;
      if (typeof val === 'object' && val.$oid) return String(val.$oid);
      return String(val);
    } catch { return String(val || ''); }
  };
  const ref6 = (val) => String(idToString(val)).slice(-6);

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );



  // Real-time filtering for Items tab
  const filterItems = (searchValue) => {
    if (!items.length) return;
    
    let filtered = [...items];
    
    if (!searchValue) {
      setFilteredItems(filtered);
      return;
    }
    
    // Handle special filter options
    if (searchValue === 'active') {
      filtered = filtered.filter(item => item.status === 'active');
    } else if (searchValue === 'pending') {
      filtered = filtered.filter(item => item.status === 'pending');
    } else if (searchValue === 'flagged') {
      filtered = filtered.filter(item => item.status === 'flagged');
    } else if (searchValue === 'featured') {
      filtered = filtered.filter(item => item.featured);
    } else {
      // Handle item ID search (check if search term looks like an ID)
      if (searchValue.toLowerCase().includes('id:')) {
        // Extract the ID part after "id:"
        const idMatch = searchValue.toLowerCase().match(/id:\s*([a-f0-9]+)/);
        if (idMatch && idMatch[1].length >= 6) {
          filtered = filtered.filter(item => {
            const itemId = item._id?.toLowerCase() || '';
            return itemId.endsWith(idMatch[1]);
          });
        }
      } else if (/^[a-fA-F0-9]+$/.test(searchValue)) {
        // Hex ID search (any length)
        if (searchValue.length >= 6) {
          // Full hex ID search - look for items ending with this
          filtered = filtered.filter(item => {
            const itemId = item._id?.toLowerCase() || '';
            return itemId.endsWith(searchValue.toLowerCase());
          });
        } else {
          // Partial hex ID search - look for items containing this anywhere
          filtered = filtered.filter(item => {
            const itemId = item._id?.toLowerCase() || '';
            return itemId.includes(searchValue.toLowerCase());
          });
        }
      } else {
        // Handle text search
        filtered = filtered.filter(item => 
          item.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.owner?.name?.toLowerCase().includes(searchValue.toLowerCase())
        );
      }
    }
    
    setFilteredItems(filtered);
  };

  // Debounced search handler for Items tab
  const handleItemsFilterChange = (value) => {
    setSearchTerm(value);
    
    // Clear existing timeout
    if (itemsSearchTimeout) {
      clearTimeout(itemsSearchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      filterItems(value);
    }, 300);
    
    setItemsSearchTimeout(timeout);
  };

  // Featured Items Functions
  const toggleFeatured = async (itemId, isFeatured) => {
    try {
      if (isFeatured) {
        await adminAPI.unfeatureItem(itemId);
        setSuccess('Item removed from featured items');
      } else {
        // Automatically assign new featured items to position 4
        await adminAPI.featureItem(itemId, 4);
        setSuccess('Item featured in position 4 (backup)');
      }
      loadAdminData();
    } catch (error) {
      setError(error.message || 'Failed to update featured status');
    }
  };

  const autoPromoteBackupItems = async () => {
    try {
      const featuredItems = items.filter(item => item.featured);
      const backupItems = featuredItems.filter(item => item.featured_rank === 4);
      
      for (const backupItem of backupItems) {
        // Find the first missing position (1, 2, or 3)
        const positions = featuredItems.map(item => item.featured_rank).filter(rank => rank <= 3);
        const missingPosition = [1, 2, 3].find(pos => !positions.includes(pos));
        
        if (missingPosition) {
          await adminAPI.updateFeaturedRank(backupItem._id, missingPosition);
        }
      }
      loadAdminData();
    } catch (error) {
      console.error('Error auto-promoting backup items:', error);
    }
  };

  const handleFeatureWithPosition = async (itemId, position) => {
    try {
      await adminAPI.featureItem(itemId, position);
      setSuccess(`Item featured in position ${position}`);
      setShowPositionModal(false);
      setSelectedItemForPosition(null);
      loadAdminData();
    } catch (error) {
      setError(error.message || 'Failed to feature item');
    }
  };

  const updateFeaturedRank = async (itemId, newRank) => {
    try {
      await adminAPI.updateFeaturedRank(itemId, newRank);
      setSuccess(`Item position updated to ${newRank}`);
      loadAdminData();
    } catch (error) {
      setError(error.message || 'Failed to update item position');
    }
  };

  const confirmFeatureWithPosition = () => {
    if (selectedItemForPosition) {
      const position = document.getElementById('position-select').value;
      handleFeatureWithPosition(selectedItemForPosition._id, parseInt(position));
    }
  };


  const filteredDisputes = disputes.filter(dispute => {
    const matchesSearch = dispute.trade_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dispute.reported_by?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = showClosedDisputes || !['resolved', 'closed'].includes(dispute.status?.toLowerCase());
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    // Sort by status: open/new first, then resolved
    const statusOrder = { 'open': 0, 'new': 0, 'pending': 1, 'resolved': 2, 'closed': 2 };
    const aOrder = statusOrder[a.status?.toLowerCase()] ?? 3;
    const bOrder = statusOrder[b.status?.toLowerCase()] ?? 3;
    return aOrder - bOrder;
  });

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content admin-content" onClick={e => e.stopPropagation()}>
        <span className="close" onClick={onClose}>&times;</span>
        
        <div className="admin-header">
          <h2>Admin Dashboard</h2>
          <button className="btn btn-secondary" onClick={onLogout}>Logout</button>
        </div>
        {error && (
          <div className="alert error" style={{background:'#ffe6e6', color:'#842029', padding:'8px 10px', border:'1px solid #f5c2c7', borderRadius:6, margin:'8px 0'}}>
            {error}
          </div>
        )}
        {success && (
          <div className="alert success" style={{background:'#e6ffed', color:'#0f5132', padding:'8px 10px', border:'1px solid #badbcc', borderRadius:6, margin:'8px 0'}}>
            {success}
          </div>
        )}
        
        <div className="admin-tabs">
          {tabs.map(tab => (
            <button 
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
              style={{position: 'relative'}}
            >
              {tab.label}
              {tab.id === 'disputes' && disputes.filter(d => d.status === 'open').length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#ff4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {disputes.filter(d => d.status === 'open').length}
                </span>
              )}
              {tab.id === 'manual-deposits' && pendingDepositsCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#ff8800',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {pendingDepositsCount}
                </span>
              )}
              {tab.id === 'withdrawals' && pendingWithdrawalsCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#ff8800',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {pendingWithdrawalsCount}
                </span>
              )}
              {tab.id === 'user-messages' && (() => {
                const unreadCount = userMessagesList.reduce((count, userMsg) => {
                  const userUnread = userMsg.messages ? userMsg.messages.filter(m => m.sender === 'user' && !m.read).length : 0;
                  return count + userUnread;
                }, 0);
                return unreadCount > 0 ? (
                  <span style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: '#dc3545',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>
                    {unreadCount}
                  </span>
                ) : null;
              })()}
              {tab.id === 'items' && pendingItemsCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#ff8800',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {pendingItemsCount}
                </span>
              )}
            </button>
          ))}
        </div>
        
        <div className="admin-content">
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="tab-content active">
              <div className="users-header">
                <h3>User Management</h3>
                <input 
                  type="text" 
                  placeholder="Search users..." 
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {selectedUser && (
                <div className="selected-user-section">
                  <div className="selected-user-header">
                    <h4>{selectedUser.name}'s Profile</h4>
                    <button 
                      className="btn btn-small"
                      onClick={() => {
                        setSelectedUser(null);
                        setUserItems([]);
                        setSelectedUserDetails(null);
                      }}
                    >
                      Back to Users
                    </button>
                  </div>
                  {selectedUserDetails && (
                    <div style={{display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:'16px', marginBottom:'16px'}}>
                      <div className="profile-info">
                        <div style={{display:'flex', gap:12, alignItems:'center', marginBottom:8}}>
                          <img src={selectedUserDetails.profile?.profile_image_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMzIiIGN5PSIzMiIgcj0iMzIiIGZpbGw9IiNmM2Y0ZjYiLz4KPGNpcmNsZSBjeD0iMzIiIGN5PSIyNCIgcj0iOCIgZmlsbD0iIzljYTNhZiIvPgo8cGF0aCBkPSJNMTYgNDhDMTYgNDQuNjg2NyAxOC42ODY3IDQyIDIyIDQySDI2QzI5LjMxMzMgNDIgMzIgNDQuNjg2NyAzMiA0OFY0OEgxNloiIGZpbGw9IiM5Y2EzYWYiLz4KPC9zdmc+'} alt="avatar" style={{width:64, height:64, borderRadius:'50%', objectFit:'cover', border:'1px solid #eee'}} />
                          <div>
                            <div style={{fontWeight:700, fontSize:18}}>{selectedUserDetails.profile?.name || 'User'}</div>
                            <div style={{color:'#555'}}>{selectedUserDetails.profile?.email}</div>
                            {selectedUserDetails.profile?.b_id && <div style={{color:'#666'}}>B-ID: {selectedUserDetails.profile.b_id}</div>}
                            <div style={{color:'#666', fontSize:'12px'}}>User ID: {selectedUserDetails.profile?.id || '—'}</div>
                          </div>
                        </div>
                        <div className="profile-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8}}>
                          <div><strong>Phone:</strong> {selectedUserDetails.profile?.phone || '—'}</div>
                          <div><strong>Closet:</strong> {selectedUserDetails.profile?.closet_name || '—'}</div>
                          <div style={{gridColumn:'1 / -1'}}><strong>Address:</strong> {selectedUserDetails.profile?.address || '—'}</div>
                          <div style={{gridColumn:'1 / -1'}}><strong>Apartment:</strong> {selectedUserDetails.profile?.apartment || '—'}</div>
                          <div><strong>City:</strong> {selectedUserDetails.profile?.city || '—'}</div>
                          <div><strong>State:</strong> {selectedUserDetails.profile?.state || '—'}</div>
                          <div><strong>ZIP:</strong> {selectedUserDetails.profile?.zip_code || '—'}</div>
                          <div><strong>Member since:</strong> {selectedUserDetails.profile?.created_at ? new Date(selectedUserDetails.profile.created_at).toLocaleDateString() : '—'}</div>
                          <div><strong>Last login:</strong> {selectedUserDetails.profile?.last_login ? new Date(selectedUserDetails.profile.last_login).toLocaleString() : '—'}</div>
                          <div><strong>Admin:</strong> {selectedUserDetails.profile?.is_admin ? 'Yes' : 'No'}</div>
                          <div><strong>Disabled:</strong> {selectedUserDetails.profile?.is_disabled ? 'Yes' : 'No'}</div>
                          <div><strong>Restricted:</strong> {selectedUserDetails.profile?.is_restricted ? 'Yes' : 'No'}</div>
                          <div><strong>Balance:</strong> {formatCurrency(selectedUserDetails.profile?.balance || 0)}</div>
                        </div>
                      </div>
                      <div className="profile-info">
                        <h4 style={{marginTop:0}}>Stats</h4>
                        <div><strong>Items:</strong> {selectedUserDetails.stats?.items_count || 0}</div>
                        <div><strong>Trades:</strong> {selectedUserDetails.stats?.trades_count || 0}</div>
                        <div><strong>Open disputes:</strong> {selectedUserDetails.stats?.open_disputes || 0}</div>
                        <div><strong>Rating:</strong> {safeNumber(selectedUserDetails.stats?.average_rating || 0).toFixed(1)} ({selectedUserDetails.stats?.rating_count || 0})</div>
                        <h4 style={{marginTop:12}}>Recent Transactions</h4>
                        <div className="table-responsive">
                          <table className="transactions-table">
                            <thead>
                              <tr>
                                <th>When</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Trade</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(allUserTransactions||[]).map(t=> (
                                <tr key={t._id}>
                                  <td>{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</td>
                                  <td>{t.type}</td>
                                  <td style={{color: t.direction==='credit' ? '#28a745' : '#dc3545', fontWeight: 'bold'}}>
                                    {t.direction==='credit' ? '+' : '-'}${(Math.abs(t.amount||0)).toFixed(2)}
                                  </td>
                                  <td>{t.trade_id ? `#${String(t.trade_id).slice(-6)}` : `#${String(t._id || '').slice(-6)}`}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {hasMoreTransactions && (
                          <div style={{marginTop: '10px', textAlign: 'center'}}>
                            <button 
                              className="btn btn-small btn-secondary"
                              onClick={() => loadMoreTransactions(selectedUser._id)}
                              disabled={loadingMoreTransactions}
                            >
                              {loadingMoreTransactions ? 'Loading...' : 'Load More Transactions'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="user-items-grid" style={{gridTemplateColumns: 'repeat(5, 1fr)'}}>
                    {userItems.length === 0 ? (
                      <p>No items found for this user.</p>
                    ) : (
                      userItems.map(item => (
                        <div key={item._id} className="user-item-card" style={{width: '100%'}}>
                          <img 
                            src={item.image_url} 
                            alt={item.name} 
                            style={{height: '140px', objectFit:'contain', background:'#f8f9fa', cursor: 'pointer'}}
                            onClick={() => openImageLightbox(item)}
                          />
                          <div className="user-item-info">
                            <h5 style={{fontSize: '13px'}}>{item.name}</h5>
                            <p>{item.description}</p>
                            <div className="user-item-details">
                              <span>Brand: {item.brand}</span>
                              <span>Size: {item.size}</span>
                              <span>Condition: {item.condition}</span>
                              <span>Value: {formatCurrency(item.value)}</span>
                            </div>
                            <div className="user-item-status">
                              <span className={`status ${item.status}`}>{item.status}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              
              {!selectedUser && (
                <div className="users-list">
                  {filteredUsers.map(user => (
                    <div key={user._id} className="user-item">
                      <div className="user-info">
                        <img src={user.profile_image || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMzIiIGN5PSIzMiIgcj0iMzIiIGZpbGw9IiNmM2Y0ZjYiLz4KPGNpcmNsZSBjeD0iMzIiIGN5PSIyNCIgcj0iOCIgZmlsbD0iIzljYTNhZiIvPgo8cGF0aCBkPSJNMTYgNDhDMTYgNDQuNjg2NyAxOC42ODY3IDQyIDIyIDQySDI2QzI5LjMxMzMgNDIgMzIgNDQuNjg2NyAzMiA0OFY0OEgxNloiIGZpbGw9IiM5Y2EzYWYiLz4KPC9zdmc+'} alt={user.name} />
                        <div>
                          <h4>{user.name || 'Unknown User'}</h4>
                          <p>{user.email}</p>
                          <p>Balance: {formatCurrency(user.balance || 0)}</p>
                        </div>
                      </div>
                      <div className="user-stats">
                        <span>Items: {(() => {
                          try {
                            const uid = user._id;
                            const cnt = (items || []).filter(it => String(it.user_id) === String(uid)).length;
                            return cnt || user.items_count || 0;
                          } catch { return user.items_count || 0; }
                        })()}</span>
                        <span>Trades: {user.trades_count || 0}</span>
                      </div>
                      <div className="user-actions">
                        <button 
                          className="btn btn-small" 
                          onClick={() => handleUserAction('view', user.name, user._id)}
                        >
                          View
                        </button>
                        <button 
                          className="btn btn-small" 
                          onClick={() => handleUserAction('message', user.name, user._id)}
                        >
                          Message
                        </button>
                      {user.is_disabled ? (
                        <button 
                          className="btn btn-small btn-success" 
                          onClick={() => handleUserAction('enable', user.name, user._id)}
                        >
                          Enable
                        </button>
                      ) : (
                        <button 
                          className="btn btn-small btn-warning" 
                          onClick={() => handleUserAction('disable', user.name, user._id)}
                        >
                          Disable
                        </button>
                      )}
                      {!user.is_disabled && !user.is_restricted && (
                        <button 
                          className="btn btn-small btn-info" 
                          onClick={() => handleUserAction('restrict', user.name, user._id)}
                        >
                          Restrict
                        </button>
                      )}
                      {user.is_restricted && (
                        <button 
                          className="btn btn-small btn-success" 
                          onClick={() => handleUserAction('enable', user.name, user._id)}
                        >
                          Unrestrict
                        </button>
                      )}
                        <button 
                          className="btn btn-small btn-danger" 
                          onClick={() => handleUserAction('delete', user.name, user._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Items Tab */}
          {activeTab === 'items' && (
            <div className="tab-content active">
              <div className="users-header">
                <h3>Item Management</h3>
                <div className="header-controls">
                  <input 
                    type="text" 
                    placeholder="Search items by name, owner, or item ID (e.g., ID: cdafba)..." 
                    className="search-input"
                    value={searchTerm}
                    onChange={(e) => handleItemsFilterChange(e.target.value)}
                  />
                  <div style={{fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic'}}>
                    💡 Tip: You can search by item name, owner name, or item ID (e.g., "ID: cdafba" or just "cdafba")
                  </div>
                  <select 
                    className="filter-select"
                    onChange={(e) => handleItemsFilterChange(e.target.value)}
                    value={searchTerm}
                  >
                    <option value="">🔍 All Items</option>
                    <option value="active">✅ Active Items</option>
                    <option value="pending">⏳ Pending Items</option>
                    <option value="flagged">🚩 Flagged Items</option>
                    <option value="featured">⭐ Featured Items</option>
                  </select>
                </div>
              </div>
              <div className="items-header">
                <div className="items-count">
                  <span className="count-badge">
                    {filteredItems.length} {searchTerm === 'active' ? 'Active' : 
                     searchTerm === 'pending' ? 'Pending' : 
                     searchTerm === 'flagged' ? 'Flagged' : 
                     searchTerm === 'featured' ? 'Featured' : 'Total'} Items
                  </span>
                </div>
              </div>
              <div className="items-list">
                {filteredItems.length === 0 ? (
                  <p>No items found.</p>
                ) : (
                  filteredItems.map((item, index) => (
                  <div key={item._id} className="item-row">
                                    <div style={modalStyles.itemNumber}>
                  <span style={modalStyles.itemNumberBadge}>ID: {ref6(item._id)}</span>
                    </div>
                    <img 
                      src={item.image_url || item.image} 
                      alt={item.name}
                      onClick={() => openImageLightbox(item)}
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      <p>Owner: {item.owner?.name || 'Unknown User'}</p>
                      <p style={{color: '#666', fontSize: '12px', marginTop: '2px'}}>{item.user_id}</p>
                      <div className="item-status-row">
                        <span className={`status ${item.status}`}>{item.status}</span>
                        {item.featured && (
                          <span className="featured-badge" style={{background: '#ffc107', color: '#000', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', marginLeft: '8px'}}>
                            Featured #{item.featured_rank}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="item-actions">
                      <button 
                        className="btn btn-small" 
                        onClick={() => handleItemAction('view', item.name, item._id)}
                      >
                        View
                      </button>
                      {item.status === 'pending' && (
                        <button 
                          className="btn btn-small btn-success" 
                          onClick={() => handleItemAction('approve', item.name, item._id)}
                        >
                          Approve
                        </button>
                      )}
                      {item.status === 'flagged' && (
                        <button 
                          className="btn btn-small btn-success" 
                          onClick={() => handleItemAction('approve', item.name, item._id)}
                        >
                          Approve
                        </button>
                      )}
                      {item.status === 'active' && (
                        <button 
                          className="btn btn-small btn-warning" 
                          onClick={() => handleItemAction('disable', item.name, item._id)}
                        >
                          Disable
                        </button>
                      )}
                      <button 
                        className="btn btn-small btn-warning" 
                        onClick={() => handleItemAction('flag', item.name, item._id)}
                      >
                        Flag
                      </button>
                      {item.featured ? (
                        <button 
                          className="btn btn-small btn-warning" 
                          onClick={() => toggleFeatured(item._id, true)}
                        >
                          Unfeature
                        </button>
                      ) : (
                        <button 
                          className="btn btn-small btn-success" 
                          onClick={() => toggleFeatured(item._id, false)}
                        >
                          Feature
                        </button>
                      )}
                      <button 
                        className="btn btn-small btn-danger" 
                        onClick={() => handleItemAction('remove', item.name, item._id)}
                      >
                        Remove
                      </button>

                    </div>
                  </div>
                ))
                )}
              </div>
            </div>
          )}

          {/* Featured Items Tab */}
          {activeTab === 'featured' && (
            <div className="tab-content active">
              <div className="users-header">
                <h3>Featured Items Management</h3>
                <div className="header-controls">
                  <div style={{fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic'}}>
                    💡 Featured items are displayed on the homepage. Position 4 acts as a backup and auto-promotes when needed.
                  </div>
                </div>
              </div>
              <div className="items-header">
                <div className="items-count">
                  <span className="count-badge">
                    {featuredItems.length} Featured Items
                  </span>
                </div>
              </div>
              <div className="items-list">
                {featuredItems.length === 0 ? (
                  <div style={{textAlign: 'center', padding: '40px 20px', color: '#666'}}>
                    <p style={{fontSize: '16px', marginBottom: '10px'}}>No featured items found.</p>
                    <p style={{fontSize: '14px', color: '#999'}}>Feature items from the "Items" tab to manage their order here.</p>
                  </div>
                ) : (
                  featuredItems.map((item, index) => (
                    <div key={item._id} className="item-row">
                      <div style={modalStyles.itemNumber}>
                        <span style={modalStyles.itemNumberBadge}>ID: {ref6(item._id)}</span>
                      </div>
                      <img src={item.image_url || item.image} alt={item.name} />
                      <div className="item-details">
                        <h4>{item.name}</h4>
                        <p>Owner: {item.owner?.name || 'Unknown User'}</p>
                        <p style={{color: '#666', fontSize: '12px', marginTop: '2px'}}>{item.user_id}</p>
                        <div className="item-status-row">
                          <span className={`status ${item.status}`}>{item.status}</span>
                          <span className="featured-badge" style={{background: '#ffc107', color: '#000', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', marginLeft: '8px'}}>
                            Featured #{item.featured_rank}
                          </span>
                        </div>
                      </div>
                      <div className="item-actions">
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                          <label style={{fontSize: '12px', color: '#666', marginRight: '4px'}}>Position:</label>
                          <input
                            type="number"
                            min="1"
                            max="4"
                            value={item.featured_rank || 4}
                            onChange={(e) => {
                              const newRank = parseInt(e.target.value);
                              if (newRank >= 1 && newRank <= 4) {
                                updateFeaturedRank(item._id, newRank);
                              }
                            }}
                            style={{
                              width: '50px',
                              padding: '4px 6px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '12px',
                              textAlign: 'center'
                            }}
                          />
                        </div>
                        <button 
                          className="btn btn-small" 
                          onClick={() => handleItemAction('view', item.name, item._id)}
                        >
                          View
                        </button>
                        <button 
                          className="btn btn-small btn-warning" 
                          onClick={() => toggleFeatured(item._id, true)}
                        >
                          Unfeature
                        </button>
                        <button 
                          className="btn btn-small btn-warning" 
                          onClick={() => handleItemAction('flag', item.name, item._id)}
                        >
                          Flag
                        </button>
                        <button 
                          className="btn btn-small btn-danger" 
                          onClick={() => handleItemAction('remove', item.name, item._id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Pickup Tracking Tab */}
          {activeTab === 'pickup-tracking' && (
            <div className="tab-content active">
              <div className="users-header">
                <h3>Pickup Exchange Tracking</h3>
                <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                  <select 
                    value={pickupStatusFilter} 
                    onChange={(e) => setPickupStatusFilter(e.target.value)}
                    style={{padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px'}}
                  >
                    <option value="all">All</option>
                    <option value="accepted">Accepted</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="disputed">Disputed</option>
                  </select>
                  <button className="btn" onClick={() => loadPickupTrades()} disabled={isLoading}>Refresh</button>
                </div>
              </div>
              <div className="pickup-trades-list">
                {pickupTrades.length === 0 ? (
                  <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                    <p>No pickup trades found.</p>
                    <p style={{fontSize: '14px', color: '#999'}}>Pickup trades will appear here when users accept trades with pickup delivery.</p>
                  </div>
                ) : (
                  pickupTrades.map(trade => {
                    const user1Received = trade.user1_received_at;
                    const user2Received = trade.user2_received_at;
                    const user1Returned = trade.user1_returned_at;
                    const user2Returned = trade.user2_returned_at;
                    
                    return (
                      <div key={trade._id} className="pickup-trade-card" style={{
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '12px',
                        background: 'white'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px'}}>
                          <div>
                            <h4 style={{margin: '0 0 8px 0', color: '#495057'}}>
                              Trade #{String(trade._id).slice(-6)}
                            </h4>
                            <div style={{fontSize: '14px', color: '#666'}}>
                              <div><strong>Items:</strong> {trade.offered_hat?.name} ↔ {trade.requested_hat?.name}</div>
                              <div><strong>Users:</strong> {trade.user1?.name} ↔ {trade.user2?.name}</div>
                              <div><strong>Pickup Date:</strong> {new Date(trade.start_date).toLocaleDateString()}</div>
                              <div><strong>Return Date:</strong> {new Date(trade.end_date).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <div style={{textAlign: 'right'}}>
                            <div style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              backgroundColor: trade.status === 'completed' ? '#d4edda' : '#fff3cd',
                              color: trade.status === 'completed' ? '#155724' : '#856404'
                            }}>
                              {trade.status}
                            </div>
                          </div>
                        </div>
                        
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px'}}>
                          <div style={{border: '1px solid #e9ecef', borderRadius: '6px', padding: '12px'}}>
                            <h5 style={{margin: '0 0 8px 0', color: '#495057'}}>{trade.user1?.name}</h5>
                            <div style={{fontSize: '12px', color: '#666'}}>
                              <div>Received: {user1Received ? '✅ ' + new Date(user1Received).toLocaleDateString() : '⏳ Pending'}</div>
                              <div>Returned: {user1Returned ? '✅ ' + new Date(user1Returned).toLocaleDateString() : '⏳ Pending'}</div>
                            </div>
                          </div>
                          <div style={{border: '1px solid #e9ecef', borderRadius: '6px', padding: '12px'}}>
                            <h5 style={{margin: '0 0 8px 0', color: '#495057'}}>{trade.user2?.name}</h5>
                            <div style={{fontSize: '12px', color: '#666'}}>
                              <div>Received: {user2Received ? '✅ ' + new Date(user2Received).toLocaleDateString() : '⏳ Pending'}</div>
                              <div>Returned: {user2Returned ? '✅ ' + new Date(user2Returned).toLocaleDateString() : '⏳ Pending'}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div style={{marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                          <button 
                            className="btn btn-small" 
                            onClick={() => openTradeDetails(trade)}
                          >
                            View Details
                          </button>
                          {trade.user1 && (
                            <button 
                              className="btn btn-small btn-primary" 
                              onClick={() => handleAdminAction('message', trade.user1.name, trade.user1._id, 'user')}
                            >
                              Message {trade.user1.name}
                            </button>
                          )}
                          {trade.user2 && (
                            <button 
                              className="btn btn-small btn-primary" 
                              onClick={() => handleAdminAction('message', trade.user2.name, trade.user2._id, 'user')}
                            >
                              Message {trade.user2.name}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Trades Tab */}
          {activeTab === 'trades' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>Trade Management</h3>
                <div style={{display:'flex', gap:8}}>
                  <select className="filter-select" onChange={(e)=>{
                    const val = e.target.value;
                    loadAdminTrades(val ? { status: val } : undefined);
                  }}>
                    <option value="">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="denied">Denied</option>
                    <option value="modified">Modified</option>
                  </select>
                  <button className="btn" onClick={()=> loadAdminTrades()} disabled={isLoading}>Refresh</button>
                </div>
              </div>
              {isLoading && trades.length === 0 ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                  <div>Loading trades...</div>
                </div>
              ) : (
                <>
                  <div style={{display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap', alignItems:'center'}}>
                <input 
                  className="search-input" 
                  placeholder="Search by Trade ID (e.g., ID: cdafba)" 
                  value={tradeSearchTerm || ''} 
                  onChange={(e) => handleManagementFilterChange('trade', e.target.value)}
                  style={{flex:'1', minWidth:'250px'}}
                />
                <input 
                  className="search-input" 
                  placeholder="Search by User ID (e.g., ID: cdafba)" 
                  value={userSearchTerm || ''} 
                  onChange={(e) => handleManagementFilterChange('user', e.target.value)}
                  style={{flex:'1', minWidth:'250px'}}
                />
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleTradeSearch()} 
                  disabled={isLoading}
                >
                  Search
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setTradeSearchTerm('');
                    setUserSearchTerm('');
                    setFilteredManagementTrades(originalTrades);
                  }} 
                  disabled={isLoading}
                >
                  Clear
                </button>
              </div>
              <div style={{fontSize: '12px', color: '#666', marginBottom: '8px', fontStyle: 'italic'}}>
                💡 Tip: You can search by Trade ID or User ID using "ID: cdafba" format or just "cdafba"
              </div>
              <div style={{margin:'8px 0'}}>
                <span className="count-badge">{Array.isArray(filteredManagementTrades) ? filteredManagementTrades.length : 0} Trades</span>
              </div>
              <div className="table-responsive">
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>Ref</th>
                      <th>Status</th>
                      <th>Delivery</th>
                      <th>User 1</th>
                      <th>User 2</th>
                      <th>Items</th>
                      <th>Created</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(filteredManagementTrades||[]).map(t => (
                      <tr key={idToString(t._id)}>
                        <td>#{ref6(t._id)}</td>
                        <td>{t.status || '—'}</td>
                        <td>{t.delivery_method || '—'}</td>
                        <td>
                          <small>
                            {ref6(t.user1_id)}
                            {t.user1_name && <br />}
                            {t.user1_name && <span style={{color: '#666'}}>{t.user1_name}</span>}
                          </small>
                        </td>
                        <td>
                          <small>
                            {ref6(t.user2_id)}
                            {t.user2_name && <br />}
                            {t.user2_name && <span style={{color: '#666'}}>{t.user2_name}</span>}
                          </small>
                        </td>
                        <td>
                          <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                            <div style={{textAlign: 'center'}}>
                              <img 
                                src={t.hat1_image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="30" height="20" viewBox="0 0 30 20"%3E%3Crect width="30" height="20" fill="%23f8f9fa"/%3E%3Ctext x="15" y="12" text-anchor="middle" fill="%23666" font-size="8"%3E1%3C/text%3E%3C/svg%3E'} 
                                alt="Item 1" 
                                style={{width: '30px', height: '20px', objectFit: 'contain', borderRadius: '2px'}}
                              />
                              <div style={{fontSize: '8px', color: '#666'}}>1</div>
                            </div>
                            <div style={{textAlign: 'center'}}>
                              <img 
                                src={t.hat2_image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="30" height="20" viewBox="0 0 30 20"%3E%3Crect width="30" height="20" fill="%23f8f9fa"/%3E%3Ctext x="15" y="12" text-anchor="middle" fill="%23666" font-size="8"%3E2%3C/text%3E%3C/svg%3E'} 
                                alt="Item 2" 
                                style={{width: '30px', height: '20px', objectFit: 'contain', borderRadius: '2px'}}
                              />
                              <div style={{fontSize: '8px', color: '#666'}}>2</div>
                            </div>
                          </div>
                        </td>
                        <td>{formatDate(t.created_at)}</td>
                        <td>{formatDate(t.start_date)}</td>
                        <td>{formatDate(t.end_date)}</td>
                        <td>
                          <div style={{display: 'flex', gap: '5px'}}>
                            <button 
                              className="btn btn-small btn-primary"
                              onClick={() => handleTradeView(t)}
                              disabled={isLoading}
                            >
                              View
                            </button>
                            {t.status !== 'completed' && t.status !== 'disputed' && (
                              <button 
                                className="btn btn-small btn-danger"
                                onClick={async () => {
                                  if (window.confirm(`Are you sure you want to delete trade #${String(t._id).slice(-6)}? This will release any holds and cannot be undone.`)) {
                                    try {
                                      setIsLoading(true);
                                      await adminAPI.deleteTrade(t._id);
                                      setSuccess('Trade deleted successfully');
                                      loadAdminTrades();
                                    } catch (error) {
                                      console.error('Delete trade error:', error);
                                      setError(error.message || 'Failed to delete trade');
                                    } finally {
                                      setIsLoading(false);
                                    }
                                  }
                                }}
                                disabled={isLoading}
                                title="Delete trade"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(!trades || trades.length === 0) && (
                      <tr><td colSpan={10} style={{textAlign:'center', color:'#666'}}>No trades found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
                </>
              )}
            </div>
          )}

          {/* Disputes Tab */}
          {activeTab === 'disputes' && (
            <div className="tab-content active">
              <div className="users-header">
                <h3>Dispute Management</h3>
                <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                  <input 
                    type="text" 
                    placeholder="Search disputes..." 
                    className="search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button 
                    className={`btn btn-small ${showClosedDisputes ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={() => setShowClosedDisputes(!showClosedDisputes)}
                  >
                    {showClosedDisputes ? 'Hide Closed' : 'Show Closed'}
                  </button>
                  <button 
                    className="btn btn-warning btn-small" 
                    onClick={async () => {
                      if (window.confirm('This will remove duplicate disputes, keeping only the most recent one per trade per user. Continue?')) {
                        try {
                          await adminAPI.cleanupDuplicateDisputes();
                          setSuccess('Duplicate disputes cleaned up successfully');
                          loadAdminData();
                        } catch (error) {
                          console.error('Cleanup error:', error);
                          if (error.message && error.message.includes('CORS')) {
                            setError('CORS error: Please ensure the backend server is running on port 5001. You can manually clean up duplicates by restarting the backend server.');
                          } else {
                            setError('Failed to cleanup duplicate disputes: ' + (error.message || 'Unknown error'));
                          }
                        }
                      }
                    }}
                  >
                    Cleanup Duplicates
                  </button>
                </div>
              </div>
              <div className="disputes-list">
                {filteredDisputes.map(dispute => (
                  <div key={dispute._id} className="dispute-item">
                    <div className="dispute-header">
                      <h4>Trade #{dispute.trade_id}</h4>
                      <span className={`status ${dispute.status.toLowerCase()}`}>
                        {dispute.status}
                      </span>
                    </div>
                    <p><strong>Issue:</strong> {dispute.issue}</p>
                    <p><strong>Reported by:</strong> {dispute.reported_by?.name}</p>
                    <p><strong>Date:</strong> {formatDate(dispute.date)}</p>
                    <div className="dispute-actions">
                      <button 
                        className="btn btn-small" 
                        onClick={() => handleAdminAction('view', `Dispute ${dispute._id}`, dispute._id, 'item')}
                      >
                        View Details
                      </button>
                      <button 
                        className="btn btn-small btn-warning" 
                        onClick={() => openDisputeMessaging(dispute._id)}
                      >
                        Message Users
                      </button>
                      <button 
                        className="btn btn-small" 
                        onClick={() => handleAdminAction('resolve', `Dispute ${dispute._id}`, dispute._id, 'dispute')}
                      >
                        Resolve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

                  {/* Balance Tab */}
        {activeTab === 'balance' && (
            <div className="tab-content active">
              <div className="users-header">
                <h3>Site Balance — All Transactions</h3>
                <div className="header-controls" style={{gap: '8px', flexWrap: 'wrap'}}>
                  <input className="search-input" placeholder="User ID, Name, or B-ID (e.g., ID: cdafba or John)" value={adminTxFilters.user_id} onChange={(e)=> handleFilterChange('user_id', e.target.value)} />
                  <input className="search-input" placeholder="Trade ID (e.g., ID: cdafba or cdafba)" value={adminTxFilters.trade_id} onChange={(e)=> handleFilterChange('trade_id', e.target.value)} />
                  <select className="filter-select" value={adminTxFilters.type} onChange={(e)=> handleFilterChange('type', e.target.value)}>
                    <option value="">Type (all)</option>
                    <option value="deposit">Deposit</option>
                    <option value="withdraw">Withdraw</option>
                    <option value="hold_reserve">Hold Reserve</option>
                    <option value="hold_refund">Hold Refund</option>
                    <option value="hold_increase">Hold Increase</option>
                    <option value="hold_decrease">Hold Decrease</option>
                    <option value="fee_charge">Site Fee</option>
                    <option value="pickup_fee">Pickup Fee</option>
                    <option value="shipping_fee">Shipping Fee</option>
                  </select>
                  <select className="filter-select" value={adminTxFilters.direction} onChange={(e)=> handleFilterChange('direction', e.target.value)}>
                    <option value="">Direction (all)</option>
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                  <input type="date" className="filter-select" value={adminTxFilters.date_from} onChange={(e)=> handleFilterChange('date_from', e.target.value)} />
                  <input type="date" className="filter-select" value={adminTxFilters.date_to} onChange={(e)=> handleFilterChange('date_to', e.target.value)} />
                  <button className="btn btn-primary" onClick={()=> loadAdminTransactions(1)} disabled={isLoading}>Apply</button>
                  <button className="btn btn-secondary" onClick={()=> { const cleared={ user_id:'', type:'', direction:'', date_from:'', date_to:'', trade_id:'' }; setAdminTxFilters({ ...cleared }); setFilteredTransactions(allTransactions); }} disabled={isLoading}>Reset</button>
                  <button className="btn btn-info" onClick={exportAdminTransactions} disabled={isLoading}>Export CSV</button>
                </div>
                <div style={{fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic'}}>
                  💡 Tip: User ID field searches User ID, Name, and B-ID. Trade ID supports "ID: cdafba" format or just "cdafba"
                </div>
                <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                  📊 Showing {filteredTransactions.length} of {allTransactions.length} transactions
                </div>
              </div>
              <div style={{display:'flex', gap: '12px', marginBottom: '10px', flexWrap:'wrap'}}>
                <div className="balance-card" style={{flex:'1', minWidth:'220px'}}>
                  <h4 style={{margin:0}}>Debits</h4>
                  <div style={{fontSize:'22px', fontWeight:700}}>{formatCurrency(adminTxData?.totals?.debit?.total || 0)}</div>
                  <small>{adminTxData?.totals?.debit?.count || 0} transactions</small>
                </div>
                <div className="balance-card" style={{flex:'1', minWidth:'220px'}}>
                  <h4 style={{margin:0}}>Credits</h4>
                  <div style={{fontSize:'22px', fontWeight:700}}>{formatCurrency(adminTxData?.totals?.credit?.total || 0)}</div>
                  <small>{adminTxData?.totals?.credit?.count || 0} transactions</small>
                </div>
                <div className="balance-card" style={{flex:'1', minWidth:'280px'}}>
                  <h4 style={{margin:0}}>One-Time Refund</h4>
                  <div className="form-row" style={{display:'grid', gridTemplateColumns:'1fr 160px', gap:8, marginTop:6}}>
                    <input className="search-input" placeholder="User email or ID (e.g., ID: cdafba)" value={adminTxFilters.__refund_user_id || ''} onChange={(e)=> setAdminTxFilters({...adminTxFilters, __refund_user_id: e.target.value})} />
                    <input type="number" className="search-input" style={{width:'160px'}} placeholder="Amount" min="0" step="0.01" value={adminTxFilters.__refund_amount || ''} onChange={(e)=> setAdminTxFilters({...adminTxFilters, __refund_amount: e.target.value})} />
                  </div>
                  <div className="form-row" style={{display:'grid', gridTemplateColumns:'1fr', gap:8, marginTop:6}}>
                    <input className="search-input" placeholder="Search user by name or email" value={adminTxFilters.__user_search || ''} onChange={(e)=> setAdminTxFilters({...adminTxFilters, __user_search: e.target.value})} />
                    {adminTxFilters.__user_search && (
                      <div className="users-list" style={{maxHeight: 80, overflow:'auto', border:'1px solid #eee', borderRadius:6, padding:4, background:'#fff'}}>
                        {(users||[])
                          .filter(u => {
                            const q = String(adminTxFilters.__user_search||'').toLowerCase();
                            if (!q) return false;
                            return String(u.email||'').toLowerCase().includes(q) || String(u.name||'').toLowerCase().includes(q) || String(u.b_id||'').toLowerCase().includes(q);
                          })
                          .slice(0, 8)
                          .map(u => (
                            <div key={u._id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'2px 0', borderBottom:'1px solid #f3f3f3'}}>
                              <div style={{fontSize: '12px'}}>
                                <div style={{fontWeight:600}}>{u.name||'User'}</div>
                                <div style={{color:'#666'}}>{u.email} {u.b_id ? `• ${u.b_id}` : ''}</div>
                              </div>
                              <button className="btn btn-small" style={{fontSize: '10px', padding: '2px 6px'}} onClick={()=> setAdminTxFilters({...adminTxFilters, __refund_user_id: u._id})}>Select</button>
                            </div>
                          ))}
                        {(!adminTxFilters.__user_search || (users||[]).filter(u=> String(u.email||'').toLowerCase().includes(String(adminTxFilters.__user_search||'').toLowerCase()) || String(u.name||'').toLowerCase().includes(String(adminTxFilters.__user_search||'').toLowerCase())).length===0) && (
                          <div style={{color:'#888', fontSize:11}}>Type to search users by email/name. Click Select to fill the user.</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-row" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:6}}>
                    <input className="search-input" placeholder="Reason (optional)" value={adminTxFilters.__refund_reason || ''} onChange={(e)=> setAdminTxFilters({...adminTxFilters, __refund_reason: e.target.value})} />
                    <input className="search-input" placeholder="Trade ID (optional, e.g., ID: cdafba)" value={adminTxFilters.__refund_trade_id || ''} onChange={(e)=> setAdminTxFilters({...adminTxFilters, __refund_trade_id: e.target.value})} />
                  </div>
                  <div style={{display:'flex', gap:8, marginTop:8, flexWrap:'wrap'}}>
                    <button className="btn btn-primary" disabled={isLoading || !adminTxFilters.__refund_user_id || !adminTxFilters.__refund_amount} onClick={async ()=>{
                      try {
                        setIsLoading(true); setError(''); setSuccess('');
                        const uid = resolveUserId(adminTxFilters.__refund_user_id);
                        if (!uid) throw new Error('User not found. Enter a valid 24-char ID or exact email.');
                        
                        // Process trade ID for "ID:" prefix
                        let tradeId = adminTxFilters.__refund_trade_id || '';
                        if (tradeId && tradeId.toLowerCase().includes('id:')) {
                          const idMatch = tradeId.toLowerCase().match(/id:\s*([a-f0-9]+)/);
                          if (idMatch && idMatch[1].length >= 6) {
                            tradeId = idMatch[1];
                          }
                        }
                        
                        const res = await adminAPI.refundUser({ userId: uid, amount: parseFloat(adminTxFilters.__refund_amount), reason: (adminTxFilters.__refund_reason||'').trim(), tradeId: tradeId.trim() });
                        setSuccess(`Refund issued successfully. New balance: ${formatCurrency(res?.balance ?? 0)}`);
                        try { alert('Refund issued successfully.'); } catch {}
                        const clearedFilters = { user_id:'', type:'', direction:'', date_from:'', date_to:'', trade_id:'' };
                        setAdminTxFilters({ ...adminTxFilters, ...clearedFilters, __refund_user_id:'', __refund_amount:'', __refund_reason:'', __refund_trade_id:'' });
                        loadAdminTransactions(1, clearedFilters);
                      } catch(e) {
                        setError(e.message || 'Failed to issue refund');
                        try { alert(e.message || 'Failed to issue refund'); } catch {}
                      } finally {
                        setIsLoading(false);
                      }
                    }}>Refund</button>
                    <button className="btn btn-success" disabled={isLoading || !adminTxFilters.__refund_user_id || !adminTxFilters.__refund_amount} onClick={async ()=>{
                      try {
                        setIsLoading(true); setError(''); setSuccess('');
                        const uid = resolveUserId(adminTxFilters.__refund_user_id);
                        if (!uid) throw new Error('User not found. Enter a valid 24-char ID or exact email.');
                        
                        // Process trade ID for "ID:" prefix
                        let tradeId = adminTxFilters.__refund_trade_id || '';
                        if (tradeId && tradeId.toLowerCase().includes('id:')) {
                          const idMatch = tradeId.toLowerCase().match(/id:\s*([a-f0-9]+)/);
                          if (idMatch && idMatch[1].length >= 6) {
                            tradeId = idMatch[1];
                          }
                        }
                        
                        const res = await adminAPI.adjustBalance({ userId: uid, amount: parseFloat(adminTxFilters.__refund_amount), direction: 'credit', reason: (adminTxFilters.__refund_reason||'').trim(), tradeId: tradeId.trim() });
                        setSuccess(`Funds credited successfully. New balance: ${formatCurrency(res?.balance ?? 0)}`);
                        try { alert('Funds credited successfully.'); } catch {}
                        const clearedFilters = { user_id:'', type:'', direction:'', date_from:'', date_to:'', trade_id:'' };
                        setAdminTxFilters({ ...adminTxFilters, ...clearedFilters, __refund_user_id:'', __refund_amount:'', __refund_reason:'', __refund_trade_id:'' });
                        loadAdminTransactions(1, clearedFilters);
                      } catch(e) {
                        setError(e.message || 'Failed to credit funds');
                        try { alert(e.message || 'Failed to credit funds'); } catch {}
                      } finally { setIsLoading(false); }
                    }}>Credit</button>
                    <button className="btn btn-warning" disabled={isLoading || !adminTxFilters.__refund_user_id || !adminTxFilters.__refund_amount} onClick={async ()=>{
                      try {
                        setIsLoading(true); setError(''); setSuccess('');
                        const uid = resolveUserId(adminTxFilters.__refund_user_id);
                        if (!uid) throw new Error('User not found. Enter a valid 24-char ID or exact email.');
                        
                        // Process trade ID for "ID:" prefix
                        let tradeId = adminTxFilters.__refund_trade_id || '';
                        if (tradeId && tradeId.toLowerCase().includes('id:')) {
                          const idMatch = tradeId.toLowerCase().match(/id:\s*([a-f0-9]+)/);
                          if (idMatch && idMatch[1].length >= 6) {
                            tradeId = idMatch[1];
                          }
                        }
                        
                        const res = await adminAPI.adjustBalance({ userId: uid, amount: parseFloat(adminTxFilters.__refund_amount), direction: 'debit', reason: (adminTxFilters.__refund_reason||'').trim(), tradeId: tradeId.trim() });
                        setSuccess(`Funds debited successfully. New balance: ${formatCurrency(res?.balance ?? 0)}`);
                        try { alert('Funds debited successfully.'); } catch {}
                        const clearedFilters = { user_id:'', type:'', direction:'', date_from:'', date_to:'', trade_id:'' };
                        setAdminTxFilters({ ...adminTxFilters, ...clearedFilters, __refund_user_id:'', __refund_amount:'', __refund_reason:'', __refund_trade_id:'' });
                        loadAdminTransactions(1, clearedFilters);
                      } catch(e) {
                        setError(e.message || 'Failed to debit funds');
                        try { alert(e.message || 'Failed to debit funds'); } catch {}
                      } finally { setIsLoading(false); }
                    }}>Debit</button>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>User</th>
                      <th>Name</th>
                      <th>B-ID</th>
                      <th>Direction</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Trade</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(filteredTransactions||[]).map(tx => (
                      <tr key={tx._id}>
                        <td>{tx.created_at ? new Date(tx.created_at).toLocaleString() : ''}</td>
                        <td>{tx.user_id}</td>
                        <td>{tx.user_name || 'N/A'}</td>
                        <td>{tx.user_b_id || ''}</td>
                        <td>{tx.direction}</td>
                        <td>
                          {tx.type === 'shipping_fee' && tx.delivery_method === 'pickup' ? 'Pickup Fee' :
                           tx.type === 'shipping_fee' && tx.delivery_method === 'shipping' ? 'Shipping Fee' :
                           tx.type === 'shipping_fee' ? 'Delivery Fee' :
                           tx.type === 'shipping_fee_refund' && tx.delivery_method === 'pickup' ? 'Pickup Fee Refund' :
                           tx.type === 'shipping_fee_refund' && tx.delivery_method === 'shipping' ? 'Shipping Fee Refund' :
                           tx.type === 'shipping_fee_refund' ? 'Delivery Fee Refund' :
                           tx.type}
                        </td>
                        <td style={{color: tx.direction==='credit' ? '#28a745' : '#dc3545', fontWeight: 'bold'}}>
                          {tx.direction==='credit' ? '+' : '-'}${(Math.abs(tx.amount||0)).toFixed(2)}
                        </td>
                        <td>{tx.trade_id ? `#${String(tx.trade_id).slice(-6)}` : `#${String(tx._id || '').slice(-6)}`}</td>
                        <td>{tx.description}</td>
                        <td>
                          {!['deposit', 'withdraw', 'hold_reserve'].includes(tx.type) && (
                            <button 
                              className="btn btn-small btn-danger"
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete transaction #${String(tx._id).slice(-6)}? This cannot be undone.`)) {
                                  try {
                                    setIsLoading(true);
                                    await adminAPI.deleteTransaction(tx._id);
                                    setSuccess('Transaction deleted successfully');
                                    loadAdminTransactions(adminTxPage);
                                  } catch (error) {
                                    setError(error.message || 'Failed to delete transaction');
                                  } finally {
                                    setIsLoading(false);
                                  }
                                }
                              }}
                              disabled={isLoading}
                              title="Delete transaction"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginTop:10}}>
                <button className="btn" onClick={()=> loadAdminTransactions(Math.max(1, adminTxPage-1))} disabled={isLoading || adminTxPage<=1}>Prev</button>
                <span style={{alignSelf:'center'}}>Page {adminTxData.page || adminTxPage}</span>
                <button className="btn" onClick={()=> {
                  const next = (adminTxData.page||adminTxPage) + 1;
                  const maxPage = Math.ceil((adminTxData.total || 0) / (adminTxData.per_page || 50));
                  if (next <= maxPage) loadAdminTransactions(next);
                }} disabled={isLoading || ((adminTxData.page||adminTxPage) >= Math.ceil((adminTxData.total || 0) / (adminTxData.per_page || 50)))}>Next</button>
              </div>
            </div>
          )}

          {/* Withdrawals Tab */}
          {activeTab === 'withdrawals' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', gap:8, alignItems:'center'}}>
                <h3 style={{marginRight:'auto'}}>User Withdrawals</h3>
                <select className="filter-select" value={withdrawStatus} onChange={(e)=> setWithdrawStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                </select>
                <button className="btn" onClick={loadWithdrawals} disabled={isLoading}>Refresh</button>
              </div>
              <div className="table-responsive">
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>User</th>
                      <th>Amount</th>
                      <th>Note</th>
                      <th>Status</th>
                      <th>Method</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(withdrawals||[]).map(w => (
                      <tr key={w._id}>
                        <td>{w.created_at ? new Date(w.created_at).toLocaleString() : ''}</td>
                        <td><div style={{display:'grid'}}><span>{w.user_name || w.user_email || w.user_id}</span><small>{w.user_id}</small></div></td>
                        <td>{formatCurrency(w.amount)}</td>
                        <td>{w.note || ''}</td>
                        <td><span className={`status ${String(w.status).toLowerCase()}`}>{w.status}</span></td>
                        <td>{w.method || 'manual'}</td>
                        <td style={{display:'flex', gap:6}}>
                          <button className="btn btn-small btn-success" disabled={isLoading || w.status==='paid'} onClick={async ()=>{
                            try { setIsLoading(true); setError(''); setSuccess(''); await adminAPI.markWithdrawalPaid(w._id); setSuccess('Marked paid'); loadWithdrawals(); } catch(e) { setError(e.message||'Failed'); } finally { setIsLoading(false); }
                          }}>Mark Paid</button>
                          <button className="btn btn-small btn-warning" disabled={isLoading || w.status==='failed'} onClick={async ()=>{
                            const admin_note = prompt('Reason (optional):','');
                            try { setIsLoading(true); setError(''); setSuccess(''); await adminAPI.markWithdrawalFailed(w._id, admin_note||''); setSuccess('Marked failed and refunded'); loadWithdrawals(); } catch(e) { setError(e.message||'Failed'); } finally { setIsLoading(false); }
                          }}>Mark Failed</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Manual Deposits Tab */}
          {activeTab === 'manual-deposits' && (
            <div className="tab-content active">
              <ManualDepositsAdmin />
            </div>
          )}

          {/* User Messages Tab */}
          {activeTab === 'user-messages' && (
            <div className="tab-content active">
              <div className="users-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <h3 style={{margin:0}}>User Messages</h3>
                <button className="btn" onClick={loadUserMessages} disabled={isLoading}>Refresh</button>
              </div>

              {!selectedUserForMessages ? (
                <div style={{marginTop: '20px'}}>
                  {userMessagesList && userMessagesList.length > 0 ? (
                    <div className="table-responsive">
                      <table className="transactions-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Last Message</th>
                            <th>Unread</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userMessagesList.map((userMsg) => {
                            const unreadCount = userMsg.messages ? userMsg.messages.filter(m => m.sender === 'user' && !m.read).length : 0;
                            const lastMessage = userMsg.messages && userMsg.messages.length > 0 
                              ? userMsg.messages[userMsg.messages.length - 1] 
                              : null;
                            return (
                              <tr key={userMsg.user_id}>
                                <td>{userMsg.user_name || 'Unknown'}</td>
                                <td>{userMsg.user_email || 'N/A'}</td>
                                <td>
                                  {lastMessage ? (
                                    <div>
                                      <div style={{fontSize: '12px', color: '#666'}}>
                                        {new Date(lastMessage.timestamp || lastMessage.created_at).toLocaleString()}
                                      </div>
                                      <div style={{fontSize: '13px', marginTop: '4px'}}>
                                        {lastMessage.message || lastMessage.content || ''}
                                      </div>
                                    </div>
                                  ) : 'No messages'}
                                </td>
                                <td>
                                  {unreadCount > 0 ? (
                                    <span style={{
                                      background: '#dc3545',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: 'bold'
                                    }}>
                                      {unreadCount}
                                    </span>
                                  ) : (
                                    <span style={{color: '#666'}}>0</span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn"
                                    style={{background: '#007bff', color: '#fff', padding: '6px 12px'}}
                                    onClick={() => {
                                      setSelectedUserForMessages(userMsg.user_id);
                                      loadUserMessageThread(userMsg.user_id);
                                    }}
                                  >
                                    View Thread
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                      <i className="fas fa-envelope" style={{fontSize: '48px', marginBottom: '16px', opacity: 0.3}}></i>
                      <p>No user messages yet</p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{marginTop: '20px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px'}}>
                    <button
                      className="btn"
                      style={{background: '#6c757d', color: '#fff'}}
                      onClick={() => {
                        setSelectedUserForMessages(null);
                        setUserMessageThread([]);
                        setReplyMessage('');
                      }}
                    >
                      ← Back to List
                    </button>
                    <h4 style={{margin: 0}}>Conversation Thread</h4>
                  </div>

                  {/* Message Thread */}
                  <div style={{maxHeight: '400px', overflowY: 'auto', padding: '15px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '20px'}}>
                    {userMessageThread && userMessageThread.length > 0 ? (
                      [...userMessageThread].sort((a, b) => new Date(b.timestamp || b.created_at || 0) - new Date(a.timestamp || a.created_at || 0)).map((msg, index) => (
                        <div
                          key={msg._id || index}
                          style={{
                            marginBottom: '15px',
                            padding: '12px',
                            borderRadius: '8px',
                            background: msg.sender === 'user' ? '#e3f2fd' : '#fff3cd',
                            borderLeft: `4px solid ${msg.sender === 'user' ? '#2196f3' : '#ffc107'}`,
                          }}
                        >
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '5px'}}>
                            <strong style={{color: msg.sender === 'user' ? '#1976d2' : '#f57c00'}}>
                              {msg.sender === 'user' ? 'User' : 'Admin'}
                            </strong>
                            <span style={{fontSize: '12px', color: '#666'}}>
                              {new Date(msg.timestamp || msg.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p style={{margin: 0, whiteSpace: 'pre-wrap'}}>{msg.message || msg.content}</p>
                        </div>
                      ))
                    ) : (
                      <p style={{textAlign: 'center', color: '#666'}}>No messages in thread</p>
                    )}
                  </div>

                  {/* Reply Form */}
                  <div style={{padding: '20px', background: '#fff', borderRadius: '8px', border: '1px solid #dee2e6'}}>
                    <h4 style={{margin: '0 0 15px 0'}}>Reply to User</h4>
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply here..."
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        marginBottom: '10px'
                      }}
                      disabled={isLoading}
                    />
                    <div style={{display: 'flex', gap: '10px'}}>
                      <button
                        onClick={() => handleReplyToUser(selectedUserForMessages)}
                        disabled={isLoading || !replyMessage.trim()}
                        className="btn"
                        style={{background: '#28a745', color: '#fff'}}
                      >
                        {isLoading ? 'Sending...' : 'Send Reply'}
                      </button>
                      <button
                        onClick={async () => {
                          await adminAPI.markUserMessageRead(selectedUserForMessages);
                          await loadUserMessages();
                          await loadUserMessageThread(selectedUserForMessages);
                        }}
                        className="btn"
                        style={{background: '#6c757d', color: '#fff'}}
                        disabled={isLoading}
                      >
                        Mark as Read
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Deleted Accounts Tab */}
          {activeTab === 'deleted-accounts' && (
            <div className="tab-content active">
              <DeletedAccountsAdmin />
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="tab-content active">
              <h3>Platform Settings</h3>
              <div className="settings-section">
                <div className="form-group">
                  <label>Trade Processing Fee (USD)</label>
                  <div className="form-row">
                    <input
                      type="number"
                      value={tradeFee}
                      onChange={(e) => setTradeFee(e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="5.00"
                    />
                    <button
                      className="btn btn-primary"
                      onClick={async () => {
                        setIsLoading(true);
                        setError('');
                        setSuccess('');
                        try {
                          const res = await adminAPI.updateTradeFee(parseFloat(tradeFee));
                          setTradeFee(res.trade_fee);
                          setSuccess(`Trade fee updated to ${formatCurrency(res.trade_fee)}`);
                        } catch (err) {
                          setError(err.message || 'Failed to update trade fee');
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={isLoading}
                    >
                      Save
                    </button>
                  </div>
                  <small className="form-hint">This platform fee is charged to both users when a trade is accepted.</small>
                </div>

                <div className="form-group">
                  <label>Pickup Convenience Fee (USD)</label>
                  <div className="form-row">
                    <input
                      type="number"
                      value={pickupFee}
                      onChange={(e) => setPickupFee(e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="5.00"
                    />
                    <button
                      className="btn btn-primary"
                      onClick={async () => {
                        setIsLoading(true);
                        setError('');
                        setSuccess('');
                        try {
                          const res = await adminAPI.updatePickupFee(parseFloat(pickupFee));
                          setPickupFee(res.pickup_fee);
                          setSuccess(`Pickup fee updated to ${formatCurrency(res.pickup_fee)}`);
                        } catch (err) {
                          setError(err.message || 'Failed to update pickup fee');
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={isLoading}
                    >
                      Save
                    </button>
                  </div>
                  <small className="form-hint">Applied only to pickup trades and charged when accepted.</small>
                </div>

                <div className="form-group">
                  <label>First Trade Free Promotion</label>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={firstTradeFreeEnabled}
                        onChange={(e) => setFirstTradeFreeEnabled(e.target.checked)}
                      />
                      <span>Enable First Trade Free Promotion</span>
                    </label>
                  </div>
                  {firstTradeFreeEnabled && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>Start Date</label>
                        <input
                          type="date"
                          value={firstTradeFreeStartDate}
                          onChange={(e) => setFirstTradeFreeStartDate(e.target.value)}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>End Date</label>
                        <input
                          type="date"
                          value={firstTradeFreeEndDate}
                          onChange={(e) => setFirstTradeFreeEndDate(e.target.value)}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                      </div>
                    </div>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      setIsLoading(true);
                      setError('');
                      setSuccess('');
                      try {
                        // Convert dates to ISO strings
                        const startDateISO = firstTradeFreeStartDate ? new Date(firstTradeFreeStartDate).toISOString() : null;
                        const endDateISO = firstTradeFreeEndDate ? new Date(firstTradeFreeEndDate).toISOString() : null;
                        
                        await adminAPI.updateFirstTradeFreePromo({
                          enabled: firstTradeFreeEnabled,
                          start_date: startDateISO,
                          end_date: endDateISO
                        });
                        if (firstTradeFreeEnabled) {
                          const periodText = firstTradeFreeStartDate && firstTradeFreeEndDate 
                            ? ` from ${new Date(firstTradeFreeStartDate).toLocaleDateString()} to ${new Date(firstTradeFreeEndDate).toLocaleDateString()}`
                            : '';
                          setSuccess(`✅ Settings saved! Promotion is active${periodText}. New users' first trade will have fees waived during this period.`);
                        } else {
                          setSuccess('✅ Settings saved! Promotion is now disabled.');
                        }
                      } catch (err) {
                        setError(err.message || 'Failed to update promotion settings');
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                    style={{ marginTop: '12px' }}
                  >
                    Save Promotion Settings
                  </button>
                  {success && success.includes('Promotion') && (
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      backgroundColor: '#d4edda',
                      border: '1px solid #28a745',
                      color: '#155724',
                      fontSize: '0.9rem'
                    }}>
                      {success}
                    </div>
                  )}
                  {/* Promotion Status Indicator */}
                  {(() => {
                    const now = new Date();
                    const nowUTC = new Date(now.toISOString());
                    
                    let status = 'inactive';
                    let statusText = 'Inactive';
                    let statusColor = '#6c757d';
                    let statusBg = '#f8f9fa';
                    let statusBorder = '#dee2e6';
                    
                    if (firstTradeFreeEnabled) {
                      const startDate = firstTradeFreeStartDate ? new Date(firstTradeFreeStartDate + 'T00:00:00Z') : null;
                      const endDate = firstTradeFreeEndDate ? new Date(firstTradeFreeEndDate + 'T23:59:59Z') : null;
                      
                      if (startDate && nowUTC < startDate) {
                        status = 'scheduled';
                        statusText = 'Scheduled';
                        statusColor = '#856404';
                        statusBg = '#fff3cd';
                        statusBorder = '#ffeaa7';
                      } else if (endDate && nowUTC > endDate) {
                        status = 'expired';
                        statusText = 'Expired';
                        statusColor = '#721c24';
                        statusBg = '#f8d7da';
                        statusBorder = '#f5c6cb';
                      } else {
                        status = 'active';
                        statusText = 'Active';
                        statusColor = '#155724';
                        statusBg = '#d4edda';
                        statusBorder = '#c3e6cb';
                      }
                    }
                    
                    return (
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        backgroundColor: statusBg,
                        border: `1px solid ${statusBorder}`,
                        color: statusColor,
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '1.1rem' }}>
                          {status === 'active' && '✅'}
                          {status === 'scheduled' && '⏰'}
                          {status === 'expired' && '❌'}
                          {status === 'inactive' && '⚪'}
                        </span>
                        <span><strong>Status:</strong> {statusText}</span>
                      </div>
                    );
                  })()}
                  <small className="form-hint">When enabled, new users' first completed trade will have site fee and shipping fees waived.</small>
                </div>



              </div>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="tab-content active">
              <div className="profile-header">
                <h3>Admin Profile</h3>
                <p>Manage your admin account settings</p>
              </div>
              <div className="profile-info">
                <div className="profile-item">
                  <label>Role:</label>
                  <span>Administrator</span>
                </div>
                <div className="profile-item">
                  <label>Permissions:</label>
                  <span>Full system access</span>
                </div>
                <div className="profile-actions">
                  <div className="profile-left">
                    <div className="profile-avatar">
                      <img src={profileImagePreview || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMzIiIGN5PSIzMiIgcj0iMzIiIGZpbGw9IiNmM2Y0ZjYiLz4KPGNpcmNsZSBjeD0iMzIiIGN5PSIyNCIgcj0iOCIgZmlsbD0iIzljYTNhZiIvPgo8cGF0aCBkPSJNMTYgNDhDMTYgNDQuNjg2NyAxOC42ODY3IDQyIDIyIDQySDI2QzI5LjMxMzMgNDIgMzIgNDQuNjg2NyAzMiA0OFY0OEgxNloiIGZpbGw9IiM5Y2EzYWYiLz4KPC9zdmc+'} alt="Profile" />
                      <div className="form-group">
                        <label>Upload Profile Photo</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (!file) return;
                            if (file.size > 5 * 1024 * 1024) {
                              setError('Profile image must be less than 5MB');
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = (ev) => setProfileImagePreview(ev.target.result);
                            reader.readAsDataURL(file);
                          }}
                        />
                      </div>
                    </div>
                    <div style={{display:'flex', gap:8}}>
                      <button 
                        onClick={() => setShowPasswordModal(true)}
                        className="btn btn-secondary"
                      >
                        Change Password
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={async ()=>{
                          if (!window.confirm('Are you sure you want to close your account? This will disable your profile and remove your items.')) return;
                          setIsLoading(true); setError(''); setSuccess('');
                          try {
                            await profileAPI.deleteAccount();
                            setSuccess('Account closed. You will be logged out.');
                            setTimeout(()=>{ onLogout && onLogout(); }, 1200);
                          } catch (e) {
                            setError(e.message || 'Failed to close account');
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                      >
                        Close Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Item Details Modal */}
          {showItemModal && selectedItem && (
            <div className="modal-overlay">
              <div className="modal-content item-details-modal" onClick={e => e.stopPropagation()}>
                <span className="close" onClick={() => setShowItemModal(false)}>&times;</span>
                <h3>Item Details</h3>
                <div className="item-details-content">
                  <div className="item-image">
                    {(() => {
                      // Prepare all images (main + additional)
                      const allImages = [];
                      if (selectedItem.image_url) {
                        allImages.push(selectedItem.image_url);
                      }
                      if (selectedItem.image && selectedItem.image !== selectedItem.image_url) {
                        allImages.push(selectedItem.image);
                      }
                      if (selectedItem.additional_images && selectedItem.additional_images.length > 0) {
                        allImages.push(...selectedItem.additional_images);
                      }
                      
                      return (
                        <>
                          <img 
                            src={allImages.length > 0 ? allImages[currentImageIndex] : ''} 
                            alt={selectedItem.name} 
                            style={{width: '100%', height: '250px', objectFit: 'contain', borderRadius: '8px'}}
                          />
                          {/* Image thumbnails */}
                          {allImages.length > 1 && (
                            <div className="image-thumbnails" style={{marginTop: '10px', display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                              {allImages.map((image, index) => (
                                <div 
                                  key={index}
                                  className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                  onClick={() => setCurrentImageIndex(index)}
                                  style={{
                                    width: '50px', 
                                    height: '50px', 
                                    border: index === currentImageIndex ? '2px solid #007bff' : '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    overflow: 'hidden'
                                  }}
                                >
                                  <img 
                                    src={image} 
                                    alt={`${selectedItem.name} ${index + 1}`}
                                    style={{width: '100%', height: '100%', objectFit: 'cover'}}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="item-info">
                    <h4>{selectedItem.name}</h4>
                    <p><strong>Description:</strong> {selectedItem.description}</p>
                    <p><strong>Brand:</strong> {selectedItem.brand}</p>
                    <p><strong>Size:</strong> {selectedItem.size}</p>
                    <p><strong>Condition:</strong> {selectedItem.condition}</p>
                    <p><strong>Value:</strong> {formatCurrency(selectedItem.value)}</p>
                    <p><strong>Status:</strong> <span className={`status ${selectedItem.status}`}>{selectedItem.status}</span></p>
                    
                    <p><strong>Owner:</strong> {selectedItem.owner?.name || 'Unknown User'}</p>
                    <p style={{color: '#666', fontSize: '12px', marginTop: '2px', marginLeft: '20px'}}>{selectedItem.user_id}</p>
                    <p><strong>Created:</strong> {formatDate(selectedItem.created_at)}</p>
                  </div>
                </div>
                <div className="item-actions">
                  <button 
                    className="btn btn-info" 
                    onClick={() => {
                      setMessageRecipient({ 
                        id: selectedItem.user_id, 
                        name: selectedItem.owner?.name || 'Unknown User' 
                      });
                      setShowItemModal(false);
                      setShowMessageModal(true);
                    }}
                    style={{marginRight: '10px'}}
                  >
                    <i className="fas fa-envelope"></i> Message User
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      showUserProfile({ 
                        _id: selectedItem.user_id, 
                        name: selectedItem.owner?.name || 'Unknown User' 
                      });
                    }}
                    style={{marginRight: '10px'}}
                  >
                    <i className="fas fa-user"></i> View Profile
                  </button>
                  {selectedItem.status === 'pending' && (
                    <button 
                      className="btn btn-success" 
                      onClick={() => {
                        handleAdminAction('approve', selectedItem.name, selectedItem._id, 'item');
                        setShowItemModal(false);
                      }}
                    >
                      Approve
                    </button>
                  )}
                  {selectedItem.status === 'active' && (
                    <button 
                      className="btn btn-warning" 
                      onClick={() => {
                        handleAdminAction('disable', selectedItem.name, selectedItem._id, 'item');
                        setShowItemModal(false);
                      }}
                    >
                      Disable
                    </button>
                  )}
                  <button 
                    className="btn btn-warning" 
                    onClick={() => {
                      handleAdminAction('flag', selectedItem.name, selectedItem._id, 'item');
                      setShowItemModal(false);
                    }}
                  >
                    Flag
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => {
                      handleAdminAction('remove', selectedItem.name, selectedItem._id, 'item');
                      setShowItemModal(false);
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* User Profile Modal */}
          {showUserProfileModal && selectedUserForProfile && userProfileData && (
            <div className="modal-overlay">
              <div className="modal-content user-profile-modal" onClick={e => e.stopPropagation()}>
                <span className="close" onClick={() => setShowUserProfileModal(false)}>&times;</span>
                <h3>User Profile</h3>
                <div className="user-profile-content">
                  <div className="user-profile-header">
                    <div className="user-profile-image">
                      <img 
                        src={userProfileData.profile_image_url || userProfileData.profile_image || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMzIiIGN5PSIzMiIgcj0iMzIiIGZpbGw9IiNmM2Y0ZjYiLz4KPGNpcmNsZSBjeD0iMzIiIGN5PSIyNCIgcj0iOCIgZmlsbD0iIzljYTNhZiIvPgo8cGF0aCBkPSJNMTYgNDhDMTYgNDQuNjg2NyAxOC42ODY3IDQyIDIyIDQySDI2QzI5LjMxMzMgNDIgMzIgNDQuNjg2NyAzMiA0OFY0OEgxNloiIGZpbGw9IiM5Y2EzYWYiLz4KPC9zdmc+'} 
                        alt={userProfileData.name}
                        style={{width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover'}}
                      />
                    </div>
                    <div className="user-profile-info">
                      <h4>{userProfileData.name}</h4>
                      <p><strong>Email:</strong> {userProfileData.email}</p>
                      <p><strong>Member Since:</strong> {formatDate(userProfileData.created_at)}</p>
                      <p><strong>Status:</strong> <span className={`status ${userProfileData.status || 'active'}`}>{userProfileData.status || 'active'}</span></p>
                    </div>
                  </div>
                  
                  <div className="user-profile-stats">
                    <div className="stat-item">
                      <span className="stat-label">Items Uploaded:</span>
                      <span className="stat-value">{userProfileData.items_count || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Trades Completed:</span>
                      <span className="stat-value">{userProfileData.trades_completed || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Account Balance:</span>
                      <span className="stat-value">{formatCurrency(userProfileData.balance || 0)}</span>
                    </div>
                    {userProfileData.rating && (
                      <div className="stat-item">
                        <span className="stat-label">Rating:</span>
                        <span className="stat-value">
                          {userProfileData.rating.toFixed(1)}/5.0
                          <span style={{color: '#ffc107', marginLeft: '5px'}}>
                            {'★'.repeat(Math.floor(userProfileData.rating))}
                            {'☆'.repeat(5 - Math.floor(userProfileData.rating))}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="user-profile-actions">
                    <button 
                      className="btn btn-info" 
                      onClick={() => {
                        setMessageRecipient(selectedUserForProfile);
                        setShowUserProfileModal(false);
                        setShowMessageModal(true);
                      }}
                    >
                      <i className="fas fa-envelope"></i> Send Message
                    </button>
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => setShowUserProfileModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
      
      {/* Password Change Modal */}
      <PasswordChangeModal 
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Trade Details Modal */}
      {showTradeModal && selectedTrade && !tradeDetails && (
        <div className="modal">
          <div className="modal-content" style={{maxWidth: '90vw', maxHeight: '90vh'}} onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowTradeModal(false)}>&times;</span>
            <h3>Trade Details - #{ref6(selectedTrade._id)}</h3>
            <div style={{textAlign: 'center', padding: '50px'}}>
              <p>Loading trade details...</p>
            </div>
          </div>
        </div>
      )}

      {showTradeModal && selectedTrade && tradeDetails && (
        <div className="modal">
          <div className="modal-content" style={{maxWidth: '90vw', maxHeight: '90vh'}} onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowTradeModal(false)}>&times;</span>
            <h3>Trade Details - #{ref6(selectedTrade._id)}</h3>
            
            {loadingTradeDetails ? (
              <div style={{textAlign: 'center', padding: '40px'}}>
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading trade details...</p>
                </div>
              </div>
            ) : (
              <div style={modalStyles.tradeDetails}>
                {/* Trade Status & Flow */}
                <div style={modalStyles.tradeInfoSection}>
                  <h4>Trade Status & Flow</h4>
                  <div style={modalStyles.infoGrid}>
                    <div style={modalStyles.infoItem}>
                      <strong>Status:</strong> <span className={`status ${selectedTrade.status}`}>{selectedTrade.status}</span>
                    </div>
                    <div style={modalStyles.infoItem}>
                      <strong>Delivery Method:</strong> {selectedTrade.delivery_method || '—'}
                    </div>
                    <div style={modalStyles.infoItem}>
                      <strong>Initiator:</strong> {selectedTrade.user1_id === selectedTrade.hold_user_id ? 'User 1' : 'User 2'}
                    </div>
                    <div style={modalStyles.infoItem}>
                      <strong>Created:</strong> {formatDate(selectedTrade.created_at)}
                    </div>
                    <div style={modalStyles.infoItem}>
                      <strong>Start Date:</strong> {formatDate(selectedTrade.start_date)}
                    </div>
                    <div style={modalStyles.infoItem}>
                      <strong>End Date:</strong> {formatDate(selectedTrade.end_date)}
                    </div>
                    {selectedTrade.expires_at && (
                      <div style={modalStyles.infoItem}>
                        <strong>Expires:</strong> {formatDate(selectedTrade.expires_at)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Users Section */}
                <div style={modalStyles.tradeInfoSection}>
                  <h4>Users Involved</h4>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                    {/* User 1 */}
                    <div style={{...modalStyles.infoItem, padding: '15px'}}>
                      <h5 style={{margin: '0 0 10px 0', color: '#007bff'}}>User 1 {selectedTrade.user1_id === selectedTrade.hold_user_id ? '(Initiator)' : ''}</h5>
                      {tradeDetails?.user1 ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px'}}>
                          <img 
                            src={tradeDetails.user1.profile_image_url || tradeDetails.user1.profile_image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"%3E%3Ccircle cx="25" cy="25" r="25" fill="%23ddd"/%3E%3Ctext x="25" y="30" text-anchor="middle" fill="%23666" font-size="12"%3E?%3C/text%3E%3C/svg%3E'} 
                            alt="User 1" 
                            style={{width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover'}}
                          />
                          <div>
                            <div><strong>Name:</strong> {tradeDetails.user1.name || 'Unknown'}</div>
                            <div><strong>Email:</strong> {tradeDetails.user1.email || '—'}</div>
                            <div><strong>Closet:</strong> {tradeDetails.user1.closet_name || '—'}</div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div><strong>ID:</strong> {ref6(selectedTrade.user1_id)}</div>
                          <div><em>User details not available</em></div>
                        </div>
                      )}
                    </div>

                    {/* User 2 */}
                    <div style={{...modalStyles.infoItem, padding: '15px'}}>
                      <h5 style={{margin: '0 0 10px 0', color: '#28a745'}}>User 2 {selectedTrade.user2_id === selectedTrade.hold_user_id ? '(Initiator)' : ''}</h5>
                      {tradeDetails?.user2 ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px'}}>
                          <img 
                            src={tradeDetails.user2.profile_image_url || tradeDetails.user2.profile_image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"%3E%3Ccircle cx="25" cy="25" r="25" fill="%23ddd"/%3E%3Ctext x="25" y="30" text-anchor="middle" fill="%23666" font-size="12"%3E?%3C/text%3E%3C/svg%3E'} 
                            alt="User 2" 
                            style={{width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover'}}
                          />
                          <div>
                            <div><strong>Name:</strong> {tradeDetails.user2.name || 'Unknown'}</div>
                            <div><strong>Email:</strong> {tradeDetails.user2.email || '—'}</div>
                            <div><strong>Closet:</strong> {tradeDetails.user2.closet_name || '—'}</div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div><strong>ID:</strong> {ref6(selectedTrade.user2_id)}</div>
                          <div><em>User details not available</em></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Items Section */}
                <div style={modalStyles.tradeInfoSection}>
                  <h4>Items Being Traded</h4>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                    {/* Item 1 */}
                    <div style={{...modalStyles.infoItem, padding: '15px'}}>
                      <h5 style={{margin: '0 0 10px 0', color: '#007bff'}}>Item 1 (User 1's Item)</h5>
                      {tradeDetails?.hat1 ? (
                        <div>
                          <div style={{textAlign: 'center', marginBottom: '10px'}}>
                            <img 
                              src={tradeDetails.hat1.image_url || tradeDetails.hat1.image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="80" viewBox="0 0 120 80"%3E%3Crect width="120" height="80" fill="%23f8f9fa"/%3E%3Ctext x="60" y="45" text-anchor="middle" fill="%23666" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E'} 
                              alt={tradeDetails.hat1.name} 
                              style={{width: '120px', height: '80px', objectFit: 'contain', borderRadius: '4px'}}
                            />
                          </div>
                          <div><strong>Name:</strong> {tradeDetails.hat1.name}</div>
                          <div><strong>Brand:</strong> {tradeDetails.hat1.brand || '—'}</div>
                          <div><strong>Size:</strong> {tradeDetails.hat1.size || '—'}</div>
                          <div><strong>Condition:</strong> {tradeDetails.hat1.condition || '—'}</div>
                          {tradeDetails.hat1.value && (
                            <div><strong>Value:</strong> {formatCurrency(safeNumber(tradeDetails.hat1.value))}</div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div><strong>Item ID:</strong> {selectedTrade.hat1_id}</div>
                          <div style={{color: '#dc3545', fontStyle: 'italic'}}>
                            {loadingTradeDetails ? 'Loading item details...' : 'Item details not available'}
                          </div>
                          {!loadingTradeDetails && (
                            <button 
                              className="btn btn-small btn-secondary" 
                              onClick={() => loadTradeDetails(selectedTrade)}
                              style={{marginTop: '10px'}}
                            >
                              Retry Loading
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Item 2 */}
                    <div style={{...modalStyles.infoItem, padding: '15px'}}>
                      <h5 style={{margin: '0 0 10px 0', color: '#28a745'}}>Item 2 (User 2's Item)</h5>
                      {tradeDetails?.hat2 ? (
                        <div>
                          <div style={{textAlign: 'center', marginBottom: '10px'}}>
                            <img 
                              src={tradeDetails.hat2.image_url || tradeDetails.hat2.image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="80" viewBox="0 0 120 80"%3E%3Crect width="120" height="80" fill="%23f8f9fa"/%3E%3Ctext x="60" y="45" text-anchor="middle" fill="%23666" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E'} 
                              alt={tradeDetails.hat2.name} 
                              style={{width: '120px', height: '80px', objectFit: 'contain', borderRadius: '4px'}}
                            />
                          </div>
                          <div><strong>Name:</strong> {tradeDetails.hat2.name}</div>
                          <div><strong>Brand:</strong> {tradeDetails.hat2.brand || '—'}</div>
                          <div><strong>Size:</strong> {tradeDetails.hat2.size || '—'}</div>
                          <div><strong>Condition:</strong> {tradeDetails.hat2.condition || '—'}</div>
                          {tradeDetails.hat2.value && (
                            <div><strong>Value:</strong> {formatCurrency(safeNumber(tradeDetails.hat2.value))}</div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div><strong>Item ID:</strong> {selectedTrade.hat2_id}</div>
                          <div style={{color: '#dc3545', fontStyle: 'italic'}}>
                            {loadingTradeDetails ? 'Loading item details...' : 'Item details not available'}
                          </div>
                          {!loadingTradeDetails && (
                            <button 
                              className="btn btn-small btn-secondary" 
                              onClick={() => loadTradeDetails(selectedTrade)}
                              style={{marginTop: '10px'}}
                            >
                              Retry Loading
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Trade Timeline */}
                <div style={modalStyles.tradeInfoSection}>
                  <h4>Trade Timeline</h4>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    {/* Trade Created */}
                    <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #007bff'}}>
                      <strong>Created:</strong> {formatDate(selectedTrade.created_at)} - Trade initiated by {selectedTrade.user1_id === selectedTrade.hold_user_id ? 'User 1' : 'User 2'}
                    </div>
                    
                    {/* Trade Status Changes */}
                    {selectedTrade.status === 'pending' && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #ffc107'}}>
                        <strong>Status:</strong> Pending - Awaiting response from {selectedTrade.user1_id === selectedTrade.hold_user_id ? 'User 2' : 'User 1'}
                      </div>
                    )}
                    
                    {selectedTrade.status === 'accepted' && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #28a745'}}>
                        <strong>Accepted:</strong> {formatDate(selectedTrade.updated_at || selectedTrade.created_at)} - Trade accepted by {selectedTrade.user1_id === selectedTrade.hold_user_id ? 'User 2' : 'User 1'}
                      </div>
                    )}
                    
                    {selectedTrade.status === 'denied' && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #dc3545'}}>
                        <strong>Denied:</strong> {formatDate(selectedTrade.updated_at || selectedTrade.created_at)} - Trade denied by {selectedTrade.user1_id === selectedTrade.hold_user_id ? 'User 2' : 'User 1'}
                      </div>
                    )}
                    
                    {selectedTrade.status === 'cancelled' && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #6c757d'}}>
                        <strong>Cancelled:</strong> {formatDate(selectedTrade.updated_at || selectedTrade.created_at)} - Trade cancelled
                      </div>
                    )}
                    
                    {selectedTrade.status === 'modified' && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #fd7e14'}}>
                        <strong>Modified:</strong> {formatDate(selectedTrade.updated_at || selectedTrade.created_at)} - Trade terms modified
                      </div>
                    )}
                    
                    {selectedTrade.status === 'active' && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #28a745'}}>
                        <strong>Active:</strong> {formatDate(selectedTrade.updated_at || selectedTrade.created_at)} - Trade is now active
                      </div>
                    )}
                    
                    {selectedTrade.status === 'completed' && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #28a745'}}>
                        <strong>Completed:</strong> {formatDate(selectedTrade.updated_at || selectedTrade.created_at)} - Trade successfully completed
                      </div>
                    )}
                    
                    {/* Trade Period */}
                    {selectedTrade.start_date && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #17a2b8'}}>
                        <strong>Start Date:</strong> {formatDate(selectedTrade.start_date)} - Trade period begins
                      </div>
                    )}
                    
                    {selectedTrade.end_date && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #6f42c1'}}>
                        <strong>End Date:</strong> {formatDate(selectedTrade.end_date)} - Trade period ends
                      </div>
                    )}
                    
                    {/* Expiration */}
                    {selectedTrade.expires_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #e83e8c'}}>
                        <strong>Expires:</strong> {formatDate(selectedTrade.expires_at)} - Offer expires
                      </div>
                    )}
                    
                    {/* Last Updated */}
                    {selectedTrade.updated_at && selectedTrade.updated_at !== selectedTrade.created_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #6c757d'}}>
                        <strong>Last Updated:</strong> {formatDate(selectedTrade.updated_at)} - Trade information modified
                      </div>
                    )}
                    
                    {/* Item Received Acknowledgments */}
                    {selectedTrade.user1_received_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #20c997'}}>
                        <strong>Item Received:</strong> {formatDate(selectedTrade.user1_received_at)} - User 1 acknowledged receiving item
                      </div>
                    )}
                    
                    {selectedTrade.user2_received_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #20c997'}}>
                        <strong>Item Received:</strong> {formatDate(selectedTrade.user2_received_at)} - User 2 acknowledged receiving item
                      </div>
                    )}
                    
                    {/* Admin Override for Received Items */}
                    {!selectedTrade.user1_received_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #dc3545', background: '#fff5f5'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span><strong>User 1:</strong> Item not yet received</span>
                          <button 
                            className="btn btn-warning btn-small"
                            onClick={() => adminOverrideReceived(selectedTrade.user1_id, 'User 1')}
                            disabled={isLoading}
                          >
                            Admin Override: Mark Received
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {selectedTrade.user1_received_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #28a745', background: '#f8fff8'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span><strong>User 1:</strong> Item received {selectedTrade.user1_received_at ? formatDate(selectedTrade.user1_received_at) : ''}</span>
                          <span style={{color: '#28a745', fontWeight: 'bold', fontSize: '12px'}}>
                            ✓ {selectedTrade.admin_override?.action === 'received' && selectedTrade.admin_override?.user_id === selectedTrade.user1_id ? 'Admin Override Applied' : 'Confirmed'}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {!selectedTrade.user2_received_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #dc3545', background: '#fff5f5'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span><strong>User 2:</strong> Item not yet received</span>
                          <button 
                            className="btn btn-warning btn-small"
                            onClick={() => adminOverrideReceived(selectedTrade.user2_id, 'User 2')}
                            disabled={isLoading}
                          >
                            Admin Override: Mark Received
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {selectedTrade.user2_received_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #28a745', background: '#f8fff8'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span><strong>User 2:</strong> Item received {selectedTrade.user2_received_at ? formatDate(selectedTrade.user2_received_at) : ''}</span>
                          <span style={{color: '#28a745', fontWeight: 'bold', fontSize: '12px'}}>
                            ✓ {selectedTrade.admin_override?.action === 'received' && selectedTrade.admin_override?.user_id === selectedTrade.user2_id ? 'Admin Override Applied' : 'Confirmed'}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Item Returned Acknowledgments */}
                    {selectedTrade.user1_returned_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #fd7e14'}}>
                        <strong>Item Returned:</strong> {formatDate(selectedTrade.user1_returned_at)} - User 1 acknowledged returning item
                      </div>
                    )}
                    
                    {selectedTrade.user2_returned_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #fd7e14'}}>
                        <strong>Item Returned:</strong> {formatDate(selectedTrade.user2_returned_at)} - User 2 acknowledged returning item
                      </div>
                    )}
                    
                    {/* Admin Override for Returned Items */}
                    {!selectedTrade.user1_returned_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #ffc107', background: '#fffbf0'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span><strong>User 1:</strong> Item not yet returned</span>
                          <button 
                            className="btn btn-warning btn-small"
                            onClick={() => adminOverrideReturned(selectedTrade.user1_id, 'User 1')}
                            disabled={isLoading}
                          >
                            Admin Override: Mark Returned
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {selectedTrade.user1_returned_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #28a745', background: '#f8fff8'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span><strong>User 1:</strong> Item returned {selectedTrade.user1_returned_at ? formatDate(selectedTrade.user1_returned_at) : ''}</span>
                          <span style={{color: '#28a745', fontWeight: 'bold', fontSize: '12px'}}>
                            ✓ {selectedTrade.admin_override?.action === 'returned' && selectedTrade.admin_override?.user_id === selectedTrade.user1_id ? 'Admin Override Applied' : 'Confirmed'}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {!selectedTrade.user2_returned_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #ffc107', background: '#fffbf0'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span><strong>User 2:</strong> Item not yet returned</span>
                          <button 
                            className="btn btn-warning btn-small"
                            onClick={() => adminOverrideReturned(selectedTrade.user2_id, 'User 2')}
                            disabled={isLoading}
                          >
                            Admin Override: Mark Returned
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {selectedTrade.user2_returned_at && (
                      <div style={{...modalStyles.infoItem, padding: '10px', borderLeft: '4px solid #28a745', background: '#f8fff8'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span><strong>User 2:</strong> Item returned {selectedTrade.user2_returned_at ? formatDate(selectedTrade.user2_returned_at) : ''}</span>
                          <span style={{color: '#28a745', fontWeight: 'bold', fontSize: '12px'}}>
                            ✓ {selectedTrade.admin_override?.action === 'returned' && selectedTrade.admin_override?.user_id === selectedTrade.user2_id ? 'Admin Override Applied' : 'Confirmed'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Details */}
                {selectedTrade.notes && (
                  <div style={modalStyles.tradeInfoSection}>
                    <h4>Notes</h4>
                    <p style={{margin: '0', padding: '10px', background: 'white', borderRadius: '4px', border: '1px solid #dee2e6'}}>{selectedTrade.notes}</p>
                  </div>
                )}

                {/* Trade Actions */}
                <div style={modalStyles.tradeInfoSection}>
                  <h4>Admin Actions</h4>
                  <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                    <button 
                      className="btn btn-small btn-warning"
                      onClick={() => {
                        if (!selectedTrade) {
                          alert('No trade selected!');
                          return;
                        }
                        setShowTradeModal(false);
                        handleAdminAction('flag', `Trade ${selectedTrade._id}`, selectedTrade._id, 'trade');
                      }}
                      disabled={!selectedTrade}
                    >
                      Flag Trade
                    </button>
                    <button 
                      className="btn btn-small btn-danger"
                      onClick={() => {
                        if (!selectedTrade) {
                          alert('No trade selected!');
                          return;
                        }
                        setShowTradeModal(false);
                        handleAdminAction('delete', `Trade ${selectedTrade._id}`, selectedTrade._id, 'trade');
                      }}
                      disabled={!selectedTrade}
                    >
                      Delete Trade
                    </button>
                    <button 
                      className="btn btn-small btn-info"
                      onClick={() => {
                        if (!selectedTrade) {
                          alert('No trade selected!');
                          return;
                        }
                        setShowTradeModal(false);
                        if (tradeDetails?.user1) {
                          handleAdminAction('message', tradeDetails.user1.name, selectedTrade.user1_id, 'user');
                        }
                      }}
                      disabled={!selectedTrade || !tradeDetails?.user1}
                    >
                      Message User 1
                    </button>
                    <button 
                      className="btn btn-small btn-info"
                      onClick={() => {
                        if (!selectedTrade) {
                          alert('No trade selected!');
                          return;
                        }
                        setShowTradeModal(false);
                        if (tradeDetails?.user2) {
                          handleAdminAction('message', tradeDetails.user2.name, selectedTrade.user2_id, 'user');
                        }
                      }}
                      disabled={!selectedTrade || !tradeDetails?.user2}
                    >
                      Message User 2
                    </button>
                    <button 
                      className="btn btn-small btn-warning"
                      onClick={() => {
                        if (!selectedTrade) {
                          alert('No trade selected!');
                          return;
                        }
                        console.log('=== USER 1 BUTTON CLICKED ===');
                        console.log('View User 1 button clicked:', { user1: tradeDetails?.user1, tradeId: selectedTrade?.user1_id });
                        console.log('Selected trade:', selectedTrade);
                        console.log('Trade details:', tradeDetails);
                        if (tradeDetails?.user1 && selectedTrade?.user1_id) {
                          console.log('Calling viewUserTradeDetails with:', { userId: selectedTrade.user1_id, userName: tradeDetails.user1.name });
                          viewUserTradeDetails(selectedTrade.user1_id, tradeDetails.user1.name);
                        } else {
                          console.error('No user1 data available');
                          console.error('tradeDetails?.user1:', tradeDetails?.user1);
                          console.error('selectedTrade?.user1_id:', selectedTrade?.user1_id);
                          alert('No User 1 data available');
                        }
                      }}
                      disabled={!selectedTrade || !tradeDetails?.user1}
                    >
                      View User 1's Trade Details
                    </button>
                    <button 
                      className="btn btn-small btn-warning"
                      onClick={() => {
                        if (!selectedTrade) {
                          alert('No trade selected!');
                          return;
                        }
                        console.log('=== USER 2 BUTTON CLICKED ===');
                        console.log('View User 2 button clicked:', { user2: tradeDetails?.user2, tradeId: selectedTrade?.user2_id });
                        console.log('Selected trade:', selectedTrade);
                        console.log('Trade details:', tradeDetails);
                        if (tradeDetails?.user2 && selectedTrade?.user2_id) {
                          console.log('Calling viewUserTradeDetails with:', { userId: selectedTrade.user2_id, userName: tradeDetails.user2.name });
                          viewUserTradeDetails(selectedTrade.user2_id, tradeDetails.user2.name);
                        } else {
                          console.error('No user2 data available');
                          console.error('tradeDetails?.user2:', tradeDetails?.user2);
                          console.error('selectedTrade?.user2_id:', selectedTrade?.user2_id);
                          alert('No User 2 data available');
                        }
                      }}
                      disabled={!selectedTrade || !tradeDetails?.user2}
                    >
                      View User 2's Trade Details
                    </button>
                    <button 
                      className="btn btn-small btn-success"
                      onClick={async () => {
                        if (!selectedTrade) {
                          alert('No trade selected!');
                          return;
                        }
                        
                        const reason = prompt('Enter reason for force completing this trade:');
                        if (!reason) return;
                        
                        try {
                          setIsLoading(true);
                          await adminAPI.forceCompleteTrade(selectedTrade._id, 'complete', reason);
                          setSuccess('Trade force completed successfully!');
                          setShowTradeModal(false);
                          // Refresh trade list
                          loadAdminTrades();
                        } catch (error) {
                          console.error('Error force completing trade:', error);
                          setError('Failed to force complete trade');
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={!selectedTrade || isLoading}
                    >
                      Force Complete Trade
                    </button>
                    {selectedTrade && selectedTrade.status === 'accepted' && selectedTrade.delivery_method === 'shipping' && (
                      <button
                        className="btn btn-small"
                        style={{ backgroundColor: '#ffc107', color: 'black' }}
                        onClick={async () => {
                          if (!selectedTrade) return;
                          try {
                            setIsLoading(true);
                            setError('');
                            setSuccess('');
                            await tradesAPI.regenerateLabels(selectedTrade._id);
                            setSuccess('Shipping labels regenerated. Users will receive email with new label links.');
                          } catch (err) {
                            setError(err.message || 'Failed to regenerate labels');
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                        disabled={!selectedTrade || isLoading}
                        title="Purchase new shipping labels and email both users (admin only)"
                      >
                        🔄 Regenerate Labels
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div style={modalStyles.modalActions}>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowTradeModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Trade Details Modal */}
      {showUserTradeDetails && userTradeDetails && (
        <div className="modal" style={{zIndex: 10000, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <div className="modal-content" style={{maxWidth: '90vw', maxHeight: '90vh', zIndex: 10001, backgroundColor: 'white', borderRadius: '8px', padding: '20px', overflow: 'auto', position: 'relative'}} onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowUserTradeDetails(false)} style={{position: 'absolute', right: '10px', top: '10px', fontSize: '24px', cursor: 'pointer', color: '#666'}}>&times;</span>
            <h3 style={{marginTop: '0', marginBottom: '20px'}}>Trade Details - {userTradeDetails.userName}'s Perspective - #{userTradeDetails.trade._id ? String(userTradeDetails.trade._id).slice(-8) : 'N/A'}</h3>
            
            <div style={modalStyles.tradeDetails}>
              {/* Trade Status & Flow */}
              <div style={modalStyles.tradeInfoSection}>
                <h4>Trade Status & Flow</h4>
                <div style={modalStyles.infoGrid}>
                  <div style={modalStyles.infoItem}>
                    <strong>Status:</strong> <span className={`status ${userTradeDetails.trade.status}`}>{userTradeDetails.trade.status}</span>
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>Delivery Method:</strong> {userTradeDetails.trade.delivery_method}
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>Initiator:</strong> {userTradeDetails.trade.hold_user_id === userTradeDetails.userPerspective ? 'You' : 'Other user'}
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>Created:</strong> {formatDate(userTradeDetails.trade.created_at)}
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>Start Date:</strong> {formatDate(userTradeDetails.trade.start_date)}
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>End Date:</strong> {formatDate(userTradeDetails.trade.end_date)}
                  </div>
                </div>
              </div>

              {/* Financial Breakdown */}
              <div style={modalStyles.tradeInfoSection}>
                <h4>Financial Breakdown ({userTradeDetails.userName}'s View)</h4>
                {(() => {
                  const trade = userTradeDetails.trade;
                  console.log('Trade object for modal:', trade);
                  console.log('Trade financial breakdown:', trade.financial_breakdown);
                  console.log('Trade offered hat:', trade.offered_hat);
                  console.log('Trade requested hat:', trade.requested_hat);
                  
                  // Check if we have historical financial snapshot (preferred for accepted trades)
                  const financialSnapshot = trade.financial_snapshot;
                  const financialBreakdown = trade.financial_breakdown;
                  const financials = trade.financials;
                  console.log('Financial snapshot check:', { 
                    hasSnapshot: !!financialSnapshot, 
                    snapshot: financialSnapshot, 
                    hasBreakdown: !!financialBreakdown,
                    breakdown: financialBreakdown,
                    tradeStatus: trade.status,
                    tradeId: trade._id 
                  });
                  
                  // For completed trades without snapshot, use existing breakdown as historical
                  const isHistorical = (financialSnapshot && (trade.status === 'accepted' || trade.status === 'completed')) || 
                                     (trade.status === 'completed' && financialBreakdown);
                  
                  console.log('isHistorical calculation:', {
                    hasSnapshot: !!financialSnapshot,
                    isAcceptedOrCompleted: trade.status === 'accepted' || trade.status === 'completed',
                    isCompleted: trade.status === 'completed',
                    hasBreakdown: !!financialBreakdown,
                    result: isHistorical
                  });
                  
                  if (isHistorical) {
                    console.log('Using historical data');
                    
                    const initiatorId = trade.hold_user_id;
                    const youInitiated = initiatorId === userTradeDetails.userPerspective;
                    
                  // Priority 1: Use trade.financials if available (per-user data)
                  if (financials) {
                    console.log('Using trade.financials (per-user data)');
                    console.log('Current financials:', financials);
                    console.log('User perspective:', userTradeDetails.userPerspective);
                    console.log('Trade initiator:', trade.hold_user_id);
                    console.log('Trade user1:', trade.user1_id);
                    console.log('Trade user2:', trade.user2_id);
                    
                    // Check if we have the correct financial breakdown for this user's perspective
                    const isInitiator = userTradeDetails.userPerspective === trade.hold_user_id;
                    const correctFinancialBreakdown = isInitiator ? 
                      (trade.financial_breakdown_initiator || trade.financials) : 
                      (trade.financial_breakdown_receiver || trade.financials);
                    
                    console.log('Is initiator:', isInitiator);
                    console.log('Using financial breakdown:', correctFinancialBreakdown);
                    
                    const isShip = trade.delivery_method === 'shipping';
                    const siteFee = Number(correctFinancialBreakdown.site_fee || 0);
                    const deliveryFees = Number(correctFinancialBreakdown.delivery_fees || 0);
                    const holdAmount = Number(correctFinancialBreakdown.other_item_value || 0);
                    const totalDue = siteFee + deliveryFees;
                    const totalIncludingHold = totalDue + holdAmount;
                    const originalBalance = Number(correctFinancialBreakdown.original_balance || 0);
                    const endingBalance = Number(correctFinancialBreakdown.ending_balance || 0);
                      
                      return (
                        <div>
                          <div style={{background: '#e8f5e8', padding: '8px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#2d5a2d'}}>
                            📊 Using per-user financial data (trade.financials)
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}><span>Site fee</span><strong>${siteFee.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between'}}>
                            <span>{isShip ? 'Your shipping fees (round trip)' : 'Your pickup fee'}</span>
                            <strong>${deliveryFees.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}><span>Item hold</span><strong>${holdAmount.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between', borderTop:'1px solid #ccc', paddingTop:'4px', marginTop:'4px'}}>
                            <span><strong>Total due on accept</strong></span><strong>${totalDue.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}>
                            <span><strong>Total including hold</strong></span><strong>${totalIncludingHold.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between', borderTop:'1px solid #ccc', paddingTop:'4px', marginTop:'4px'}}>
                            <span>Your balance (at acceptance)</span><strong>${originalBalance.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}>
                            <span>Your balance (after trade)</span><strong>${endingBalance.toFixed(2)}</strong>
                          </div>
                        </div>
                      );
                    }
                    
                    // Priority 2: For User 1 (initiator), bypass snapshot and use financial_breakdown if available
                    if (youInitiated && financialBreakdown) {
                      console.log('User 1 (initiator) - using financial_breakdown instead of snapshot');
                      const isShip = trade.delivery_method === 'shipping';
                      const siteFee = Number(financialBreakdown.site_fee || 0);
                      const deliveryFees = Number(financialBreakdown.delivery_fees || 0);
                      const holdAmount = Number(financialBreakdown.other_item_value || 0);
                      const totalDue = siteFee + deliveryFees;
                      const totalIncludingHold = totalDue + holdAmount;
                      const originalBalance = Number(financialBreakdown.original_balance || 0);
                      const endingBalance = Number(financialBreakdown.ending_balance || 0);
                      
                      return (
                        <div>
                          <div style={{background: '#e8f5e8', padding: '8px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#2d5a2d'}}>
                            📊 Using financial breakdown data (User 1 - Initiator)
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}><span>Site fee</span><strong>${siteFee.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between'}}>
                            <span>{isShip ? 'Your shipping fees (round trip)' : 'Your pickup fee'}</span>
                            <strong>${deliveryFees.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}><span>Item hold</span><strong>${holdAmount.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between', borderTop:'1px solid #ccc', paddingTop:'4px', marginTop:'4px'}}>
                            <span><strong>Total due on accept</strong></span><strong>${totalDue.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}>
                            <span><strong>Total including hold</strong></span><strong>${totalIncludingHold.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between', borderTop:'1px solid #ccc', paddingTop:'4px', marginTop:'4px'}}>
                            <span>Your balance (at acceptance)</span><strong>${originalBalance.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}>
                            <span>Your balance (after trade)</span><strong>${endingBalance.toFixed(2)}</strong>
                          </div>
                        </div>
                      );
                    }
                    
                    // Check if we have a proper financial snapshot (for User 2 - receiver)
                    if (financialSnapshot) {
                      console.log('Using financial snapshot data');
                      const userData = youInitiated ? financialSnapshot.initiator : financialSnapshot.receiver;
                      console.log('User data from snapshot:', userData);
                      
                      if (userData) {
                        const isShip = trade.delivery_method === 'shipping';
                        const siteFee = Number(userData.platform_fee || 0);
                        const deliveryFees = isShip ? Number(userData.shipping_fee || 0) : Number(userData.pickup_fee || 0);
                        const holdAmount = Number(userData.hold_amount || 0);
                        const totalDue = Number(userData.total_due || 0);
                        const totalIncludingHold = Number(userData.total_including_hold || 0);
                        const originalBalance = Number(userData.balance_at_acceptance || 0);
                        const endingBalance = Number(userData.ending_balance || 0);
                        
                        return (
                          <div>
                            <div style={{background: '#e8f5e8', padding: '8px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#2d5a2d'}}>
                              📊 Historical snapshot from trade acceptance ({new Date(financialSnapshot.accepted_at).toLocaleString()})
                            </div>
                            <div style={{display:'flex', justifyContent:'space-between'}}><span>Site fee</span><strong>${siteFee.toFixed(2)}</strong></div>
                            <div style={{display:'flex', justifyContent:'space-between'}}>
                              <span>{isShip ? 'Your shipping fees (round trip)' : 'Your pickup fee'}</span>
                              <strong>${deliveryFees.toFixed(2)}</strong>
                            </div>
                            <div style={{display:'flex', justifyContent:'space-between'}}><span>Hold (reserve on accept)</span><strong>${holdAmount.toFixed(2)}</strong></div>
                            <div style={{display:'flex', justifyContent:'space-between', marginTop:6}}><span>Total due on accept</span><strong>${totalDue.toFixed(2)}</strong></div>
                            <div style={{display:'flex', justifyContent:'space-between'}}><span>Total incl. new hold</span><strong>${totalIncludingHold.toFixed(2)}</strong></div>
                            <div style={{display:'flex', justifyContent:'space-between', marginTop:6}}><span>Your balance (at acceptance)</span><strong>${originalBalance.toFixed(2)}</strong></div>
                            <div style={{display:'flex', justifyContent:'space-between'}}><span>Ending balance after accept</span><strong>${endingBalance.toFixed(2)}</strong></div>
                          </div>
                        );
                      }
                    } else if (trade.status === 'completed' && financialBreakdown) {
                      // For completed trades without snapshot, use existing breakdown as historical
                      console.log('Using completed trade breakdown as historical data');
                      const isShip = trade.delivery_method === 'shipping';
                      const siteFee = Number(financialBreakdown.site_fee || 0);
                      const deliveryFees = Number(financialBreakdown.delivery_fees || 0);
                      const totalDueOnAccept = siteFee + deliveryFees;
                      const originalBalance = Number(financialBreakdown.original_balance || 0);
                      const endingBalance = Number(financialBreakdown.ending_balance || 0);
                      const holdAmount = Number(financialBreakdown.other_item_value || 0);
                      const totalIncludingHold = totalDueOnAccept + holdAmount;
                      
                      return (
                        <div>
                          <div style={{background: '#e8f5e8', padding: '8px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#2d5a2d'}}>
                            📊 Historical data from completed trade
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}><span>Platform fee</span><strong>${siteFee.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between'}}>
                            <span>{isShip ? 'Your shipping fees (round trip)' : 'Your pickup fee'}</span>
                            <strong>${deliveryFees.toFixed(2)}</strong>
                          </div>
                          <div style={{display:'flex', justifyContent:'space-between'}}><span>Hold (reserve on accept)</span><strong>${holdAmount.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between', marginTop:6}}><span>Total due on accept</span><strong>${totalDueOnAccept.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between'}}><span>Total incl. new hold</span><strong>${totalIncludingHold.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between', marginTop:6}}><span>Your balance (at acceptance)</span><strong>${originalBalance.toFixed(2)}</strong></div>
                          <div style={{display:'flex', justifyContent:'space-between'}}><span>Ending balance after accept</span><strong>${endingBalance.toFixed(2)}</strong></div>
                        </div>
                      );
                    }
                  }
                  
                  // This should not happen for completed trades - they should be handled above
                  if (trade.status === 'completed') {
                    console.log('ERROR: Completed trade reached fallback - this should not happen');
                    return (
                      <div>
                        <div style={{background: '#f8d7da', padding: '8px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#721c24'}}>
                          ❌ Error: Unable to load financial data for this completed trade
                        </div>
                      </div>
                    );
                  }
                  
                  // Fallback to dynamic calculation for pending trades only
                  console.log('Falling back to dynamic calculation for pending trade - isHistorical:', isHistorical);
                  const offeredHat = trade.offered_hat || trade.hat1;
                  const requestedHat = trade.requested_hat || trade.hat2;
                  
                  console.log('Financial breakdown found:', financialBreakdown);
                  console.log('Offered hat found:', offeredHat);
                  console.log('Requested hat found:', requestedHat);
                  
                  const isShip = trade.delivery_method === 'shipping';
                  const siteFee = Number(financialBreakdown?.site_fee || 0);
                  const deliveryFees = Number(financialBreakdown?.delivery_fees || 0);
                  const totalDueOnAccept = siteFee + deliveryFees;
                  
                  // Calculate hold amount from user's perspective
                  const offered = offeredHat;
                  const requested = requestedHat;
                  const initiatorId = trade.hold_user_id;
                  const youInitiated = initiatorId === userTradeDetails.userPerspective;
                  const yourBorrowedValue = youInitiated ? Number(requested?.value || 0) : Number(offered?.value || 0);
                  const yourHoldReserved = youInitiated;
                  
                                                                            // Get running balance for this specific trade
                  const tradeBalance = userTradeDetails.runningBalances[trade._id];
                  const currentBalance = tradeBalance ? tradeBalance.startingBalance : userTradeDetails.userBalance;
                  const balanceAfterAccept = tradeBalance ? tradeBalance.endingBalance : (currentBalance - totalDueOnAccept - yourBorrowedValue);
                  
                  console.log('Modal balance calculation:', {
                    userBalance: userTradeDetails.userBalance,
                    currentBalance: currentBalance,
                    balanceAfterAccept: balanceAfterAccept,
                    tradeBalance: tradeBalance,
                    financialBreakdown: trade.financial_breakdown,
                    siteFee: siteFee,
                    deliveryFees: deliveryFees,
                    totalDueOnAccept: totalDueOnAccept,
                    yourBorrowedValue: yourBorrowedValue
                  });
                  
                  return (
                    <div>
                      <div style={{background: '#fff3cd', padding: '8px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#856404'}}>
                        ⚠️ Dynamic calculation for pending trade
                      </div>
                      <div style={{display:'flex', justifyContent:'space-between'}}><span>Platform fee</span><strong>${siteFee.toFixed(2)}</strong></div>
                      <div style={{display:'flex', justifyContent:'space-between'}}>
                        <span>{isShip ? 'Your shipping fees (round trip)' : 'Your pickup fee'}</span>
                        <strong>${deliveryFees.toFixed(2)}</strong>
                      </div>
                      <div style={{display:'flex', justifyContent:'space-between'}}><span>{yourHoldReserved ? 'Hold (already reserved)' : 'Hold (reserve on accept)'}</span><strong>${yourBorrowedValue.toFixed(2)}</strong></div>
                      <div style={{display:'flex', justifyContent:'space-between', marginTop:6}}><span>Total due on accept</span><strong>${totalDueOnAccept.toFixed(2)}</strong></div>
                      {!yourHoldReserved && (
                        <div style={{display:'flex', justifyContent:'space-between'}}><span>Total incl. new hold</span><strong>${(totalDueOnAccept + yourBorrowedValue).toFixed(2)}</strong></div>
                      )}
                      <div style={{display:'flex', justifyContent:'space-between', marginTop:6}}><span>Your balance</span><strong>${currentBalance.toFixed(2)}</strong></div>
                      <div style={{display:'flex', justifyContent:'space-between'}}><span>Ending balance after accept</span><strong>${balanceAfterAccept.toFixed(2)}</strong></div>
                      {currentBalance < (totalDueOnAccept + (yourHoldReserved ? 0 : yourBorrowedValue)) && (
                        <div style={{marginTop:6, padding:8, border:'1px solid #f5c2c7', background:'#f8d7da', color:'#842029', borderRadius:6}}>
                          Insufficient current balance. You need ${(totalDueOnAccept + (yourHoldReserved ? 0 : yourBorrowedValue)).toFixed(2)} to accept. Please add funds.
                        </div>
                      )}
                      <small style={{display:'block', color:'#666', marginTop:6}}>Each user pays their own platform and delivery fees.</small>
                      <small style={{display:'block', color:'#666'}}>A hold equal to the other item's value is reserved per user.</small>
                    </div>
                  );
                })()}
              </div>

              {/* Items Being Traded */}
              <div style={modalStyles.tradeInfoSection}>
                <h4>Items Being Traded</h4>
                <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
                  {(userTradeDetails.trade.offered_hat || userTradeDetails.trade.hat1) && (
                    <div style={{flex: '1', minWidth: '200px'}}>
                      <h5>Offered Item</h5>
                      {(() => {
                        const offeredHat = userTradeDetails.trade.offered_hat || userTradeDetails.trade.hat1;
                        return (
                          <>
                            <img 
                              src={offeredHat.image_url || offeredHat.image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="80" viewBox="0 0 120 80"%3E%3Crect width="120" height="80" fill="%23f8f9fa" stroke="%23dee2e6" stroke-width="1"/%3E%3Ctext x="60" y="45" text-anchor="middle" fill="%236c757d" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E'} 
                              alt={offeredHat.name}
                              style={{width: '120px', height: '80px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px'}}
                            />
                            <div><strong>Name:</strong> {offeredHat.name}</div>
                            <div><strong>Brand:</strong> {offeredHat.brand || '—'}</div>
                            <div><strong>Size:</strong> {offeredHat.size || '—'}</div>
                            <div><strong>Condition:</strong> {offeredHat.condition || '—'}</div>
                            {offeredHat.value && (
                              <div><strong>Value:</strong> ${Number(offeredHat.value).toFixed(2)}</div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {(userTradeDetails.trade.requested_hat || userTradeDetails.trade.hat2) && (
                    <div style={{flex: '1', minWidth: '200px'}}>
                      <h5>Requested Item</h5>
                      {(() => {
                        const requestedHat = userTradeDetails.trade.requested_hat || userTradeDetails.trade.hat2;
                        return (
                          <>
                            <img 
                              src={requestedHat.image_url || requestedHat.image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="80" viewBox="0 0 120 80"%3E%3Crect width="120" height="80" fill="%23f8f9fa" stroke="%23dee2e6" stroke-width="1"/%3E%3Ctext x="60" y="45" text-anchor="middle" fill="%236c757d" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E'} 
                              alt={requestedHat.name}
                              style={{width: '120px', height: '80px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px'}}
                            />
                            <div><strong>Name:</strong> {requestedHat.name}</div>
                            <div><strong>Brand:</strong> {requestedHat.brand || '—'}</div>
                            <div><strong>Size:</strong> {requestedHat.size || '—'}</div>
                            <div><strong>Condition:</strong> {requestedHat.condition || '—'}</div>
                            {requestedHat.value && (
                              <div><strong>Value:</strong> ${Number(requestedHat.value).toFixed(2)}</div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div style={modalStyles.modalActions}>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowUserTradeDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute Details Modal */}
      {showDisputeModal && selectedDispute && (
        <div className="modal">
          <div className="modal-content" style={{maxWidth: '90vw', maxHeight: '90vh'}} onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowDisputeModal(false)}>&times;</span>
            <h3>Dispute Details - #{ref6(selectedDispute._id)}</h3>
            
            <div style={modalStyles.tradeDetails}>
              {/* Dispute Information */}
              <div style={modalStyles.tradeInfoSection}>
                <h4>Dispute Information</h4>
                <div style={modalStyles.infoGrid}>
                  <div style={modalStyles.infoItem}>
                    <strong>Status:</strong> <span className={`status ${selectedDispute.status}`}>{selectedDispute.status}</span>
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>Issue:</strong> {selectedDispute.issue}
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>Description:</strong> {selectedDispute.description || 'No description provided'}
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>Created:</strong> {formatDate(selectedDispute.date)}
                  </div>
                  <div style={modalStyles.infoItem}>
                    <strong>Trade ID:</strong> {ref6(selectedDispute.trade_id)}
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div style={modalStyles.tradeInfoSection}>
                <h4>User Information</h4>
                {disputeDetails?.user ? (
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px'}}>
                    <img 
                      src={disputeDetails.user.profile_image_url || disputeDetails.user.profile_image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"%3E%3Ccircle cx="25" cy="25" r="25" fill="%23ddd"/%3E%3Ctext x="25" y="30" text-anchor="middle" fill="%23666" font-size="12"%3E?%3C/text%3E%3C/svg%3E'} 
                      alt="User" 
                      style={{width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover'}}
                    />
                    <div>
                      <div><strong>Name:</strong> {disputeDetails.user.name || 'Unknown'}</div>
                      <div><strong>Email:</strong> {disputeDetails.user.email || '—'}</div>
                      <div><strong>Closet:</strong> {disputeDetails.user.closet_name || '—'}</div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div><strong>User ID:</strong> {ref6(selectedDispute.user_id)}</div>
                    <div><em>User details not available</em></div>
                  </div>
                )}
              </div>

              {/* Trade Information */}
              <div style={modalStyles.tradeInfoSection}>
                <h4>Trade Information</h4>
                {disputeDetails?.trade ? (
                  <div style={modalStyles.infoGrid}>
                    <div style={modalStyles.infoItem}>
                      <strong>Trade Status:</strong> <span className={`status ${disputeDetails.trade.status}`}>{disputeDetails.trade.status}</span>
                    </div>
                    <div style={modalStyles.infoItem}>
                      <strong>Delivery Method:</strong> {disputeDetails.trade.delivery_method || '—'}
                    </div>
                    <div style={modalStyles.infoItem}>
                      <strong>Created:</strong> {formatDate(disputeDetails.trade.created_at)}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div><strong>Trade ID:</strong> {ref6(selectedDispute.trade_id)}</div>
                    <div><em>Trade details not available</em></div>
                  </div>
                )}
              </div>
            </div>
            
            <div style={modalStyles.modalActions}>
              <button 
                className="btn btn-warning" 
                onClick={() => {
                  setShowDisputeModal(false);
                  openDisputeMessaging(selectedDispute._id);
                }}
              >
                Message User
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setShowDisputeModal(false);
                  handleAdminAction('resolve', `Dispute ${selectedDispute._id}`, selectedDispute._id, 'dispute');
                }}
              >
                Resolve Dispute
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDisputeModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute Messaging Modal */}
      {showDisputeMessagingModal && selectedDisputeForMessaging && (
        <div className="modal" style={{zIndex: 10005}}>
          <div className="modal-content" style={{maxWidth: '600px', maxHeight: '80vh'}} onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowDisputeMessagingModal(false)}>&times;</span>
            <h3>Dispute Messaging</h3>
            <div style={{background: '#e3f2fd', padding: '10px', borderRadius: '5px', marginBottom: '15px'}}>
              <strong>Dispute ID:</strong> {selectedDisputeForMessaging}
            </div>
            
            <div style={{height: '400px', border: '1px solid #ddd', borderRadius: '5px', padding: '10px', marginBottom: '15px', overflowY: 'auto', background: '#f9f9f9'}}>
              {loadingDisputeMessages ? (
                <div style={{textAlign: 'center', padding: '20px'}}>Loading messages...</div>
              ) : disputeMessages.length === 0 ? (
                <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>No messages yet</div>
              ) : (
                disputeMessages.map((message, index) => (
                  <div key={index} style={{marginBottom: '15px', padding: '10px', background: 'white', borderRadius: '5px', border: '1px solid #eee'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                      <strong style={{color: message.sender === 'admin' ? '#d32f2f' : '#1976d2'}}>
                        {message.sender === 'admin' ? 'Admin' : message.user_name || message.sender_name || 'User'}
                      </strong>
                      <span style={{fontSize: '12px', color: '#666'}}>
                        {message.created_at ? formatMessageDate(message.created_at) : (message.timestamp ? formatMessageDate(message.timestamp) : 'Invalid Date')}
                      </span>
                    </div>
                    <div style={{whiteSpace: 'pre-wrap'}}>{message.message}</div>
                  </div>
                ))
              )}
            </div>
            
            <div style={{display: 'flex', gap: '10px', alignItems: 'flex-end'}}>
              <textarea
                value={newDisputeMessage}
                onChange={(e) => setNewDisputeMessage(e.target.value)}
                placeholder="Type your message here..."
                style={{flex: 1, minHeight: '60px', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', resize: 'vertical'}}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendDisputeMessage();
                  }
                }}
              />
              <button 
                className="btn btn-primary" 
                onClick={sendDisputeMessage}
                disabled={!newDisputeMessage.trim()}
              >
                Send
              </button>
            </div>
            
            <div style={{marginTop: '15px', textAlign: 'right'}}>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDisputeMessagingModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Position Selection Modal */}
      {showPositionModal && selectedItemForPosition && (
        <div className="modal">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowPositionModal(false)}>&times;</span>
            <h3>Select Featured Position for {selectedItemForPosition.name}</h3>
            <div className="position-selection">
              <p>Choose which position (1st, 2nd, 3rd, or 4th backup) this item should be featured in:</p>
              <select id="position-select" className="filter-select" style={{marginBottom: '20px'}}>
                <option value="1">1st Position</option>
                <option value="2">2nd Position</option>
                <option value="3">3rd Position</option>
                <option value="4">4th Position (Backup)</option>
              </select>
              <div className="modal-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowPositionModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={confirmFeatureWithPosition}
                >
                  Feature Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="modal">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <span className="close" onClick={() => setShowMessageModal(false)}>&times;</span>
            <h3>Send Message to {messageRecipient?.name}</h3>
            <div className="message-form">
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Enter your message..."
                rows="4"
                className="message-textarea"
              />
              <div className="message-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowMessageModal(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={sendMessage}
                  disabled={isLoading || !messageText.trim()}
                >
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        itemName={lightboxItemName}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setLightboxIndex}
      />
      
    </div>
  );
};

export default AdminModal; 
